/**
 * Per-variant per-entry Dex state store (Push 5 / v1.4.2 b1).
 *
 * This module tracks the Pokédex-only state of every (variant, dexNumber)
 * pair the user has either scanned via the Pokédex OCR pipeline OR
 * toggled manually from the Dex page. It is *strictly* a Dex-state
 * surface — it carries no inventory/instance fields (no CP, IV,
 * instanceId, nickname). That separation is the user-direction
 * requirement: "stop confusion between Pokémon the user owns and their
 * Pokédex state."
 *
 * Storage:
 *   - localStorage (key `nexus.dexState.v1`) for immediate device-local
 *     persistence — works without an authenticated server roundtrip.
 *   - The Pokédex page can flush in-memory state to the server via
 *     `flushManualOverridesToServer` when a Pro endpoint exists. We
 *     intentionally keep the server-sync path optional: a missing
 *     endpoint is a documented gap, not a hard error.
 *
 * Event model:
 *   - `DEX_STATE_EVENT` fires on every mutation so the Dex page can
 *     re-render without polling.
 */

import { useSyncExternalStore } from "react";
import type {
  PokedexVariant,
  CellState,
} from "./pogoPokedexVariantClassifier";
import {
  isDerivedVariant,
  ALL_VARIANTS,
  MANUAL_ONLY_VARIANTS,
} from "./pogoPokedexVariantClassifier";
import { resolveDexEntryConflict } from "./dexEntryResolver";
import { isCustomDexLinkageEnabled } from "./customDexLinkagePref";
import { validateDexMember, curatedRosterFor } from "./pogoAvailability";
import { isImpossibleForVariant, isVariantAvailable } from "./variantAvailability";
import { recordRejectedUnreleasedScanWrite } from "./scanIntegrity";
import {
  COMPLETE_BASE_VARIANTS,
  COMPLETE_CONDITIONAL_VARIANTS,
  computeCompletePresentSet,
} from "./dexCompleteDerivation";
import { recordSyncAttempt, reportSyncHealth } from "./dexSyncHealth";
import {
  recordEntryTombstone,
  readTombstones,
  isRowTombstoned,
  clearEntryTombstone,
} from "./dexTombstones";
import { computeFormCascade } from "./formCascade";
import { formDexCells, isFormDexVariant } from "./formDexRoster";
import {
  FORM_GROUPS_BY_SPECIES_ID,
  FORM_GROUPS_BY_FORM_ID,
} from "./generated/pokemonForms.generated";
import {
  isMegaGmaxVariant,
  MEGA_EXTRA_FORMS,
  GMAX_EXTRA_FORMS,
  dexExtraFormsFor,
} from "./megaGmaxSprites";

export const DEX_STATE_EVENT = "nexus:dex-state-changed";
const STORAGE_KEY = "nexus.dexState.v1";
const LAST_SYNC_AT_KEY = "nexus.dexState.lastSyncAt.v1";

/**
 * b19-dex-server-sync: debounce window for batched PUT flushes.
 * Every mutation enqueues to pendingDexState and reschedules a flush
 * after this many ms of inactivity. 500ms covers a full OCR scan burst
 * without firing a separate PUT per cell.
 */
export const DEX_SYNC_DEBOUNCE_MS = 500;

/**
 * Module-scope pending set. Keys are `${variant}:${dexNumber}`. The map
 * holds the latest entry for each (variant, dexNumber) pair that has been
 * mutated since the last successful PUT flush. On flush, the entire map is
 * sent as a single PUT body and then cleared on success.
 */
const pendingDexState = new Map<string, DexEntryState>();
let debounceTimer: ReturnType<typeof setTimeout> | null = null;
/**
 * b17p B1: the dex variant pill the user last had selected on the Pokedex page.
 * The hero-button live-scan quick-start reads this so the per-cell classifier
 * runs the sprite test for the variant the user is actually looking at (e.g.
 * Perfect/Purified), instead of guessing from the variant with the most stored
 * entries. Persisted to its own localStorage key so it survives a reload, and
 * mirrored in a module-level cache so reads are synchronous and SSR/test-safe.
 */
const LAST_VARIANT_KEY = "nexus.dexState.lastActiveVariant.v1";
let lastActiveVariantCache: PokedexVariant | null = null;

/**
 * One stored state record. Keyed by `${variant}:${dexNumber}` for base
 * species rows and `${variant}:${dexNumber}:${formId}` for form-level
 * rows (PR-E2c-store). Existing localStorage keys are untouched: a row
 * without formId stays at the old key shape so prior installs upgrade
 * silently.
 *
 * `source` distinguishes user-initiated overrides ("manual") from OCR-
 * derived state ("scan") for UI provenance (e.g. the small "manual"/"trade"
 * grid badges) and telemetry ONLY. Since 2026-07-18 it never ranks a write:
 * conflicts are pure last-write-wins (see dexEntryResolver), so a newer scan
 * overwrites an older manual row and vice-versa.
 */
export interface DexEntryState {
  variant: PokedexVariant;
  dexNumber: number;
  state: CellState;
  source: "scan" | "manual" | "trade";
  updatedAt: number;
  /**
   * Push 6 / v1.4.2 b2: when a manual override is created over an
   * existing scan-derived row, we record what the scan said here. If
   * the user later toggles the entry back to this baseline, the manual
   * override is automatically cleared and the row reverts to source
   * "scan" — which is what removes the small "M" badge in the Pokédex
   * grid. Optional so older stored entries deserialize cleanly.
   */
  manualBaseline?: CellState;
  /**
   * PR-E2c-store: optional form id for variant-axis sub-dex catches.
   * Absent or undefined => base-species catch (legacy behaviour, lives
   * at the old `${variant}:${dexNumber}` key). Non-empty string =>
   * form-level catch (e.g. `vivillon-meadow`) at the
   * `${variant}:${dexNumber}:${formId}` key. The key shape is computed
   * by `keyFor` so callers never assemble it themselves.
   */
  formId?: string;
}

type StoreData = Record<string, DexEntryState>;

function keyFor(
  variant: PokedexVariant,
  dexNumber: number,
  formId?: string | null,
): string {
  return formId
    ? `${variant}:${dexNumber}:${formId}`
    : `${variant}:${dexNumber}`;
}

/**
 * THE VARIANT A CELL'S TRUTH IS STORED UNDER (2026-09-01 owner report: "the
 * costume and form dexes aren't linked to the species pages ... they are one
 * and the same").
 *
 * The Regional and Costume dexes are not new facts about a Pokémon. Their cells
 * are the SAME cells the species page already draws in its Forms and Costumes
 * sections: an Alolan Meowth ticked on the Regional dex and an Alolan Meowth
 * ticked on Meowth's own page are one catch, not two. They shipped writing
 * their own `regional:52:meowth-galar` / `costume:25:pikachu-...` namespace,
 * which nothing else reads and nothing else writes, so each dex started empty
 * for a trainer whose species pages were already full — every cell a silhouette
 * because the row it reads was never the row the catch went to.
 *
 * So a form dex is a VIEW over MAIN's form rows rather than a namespace of its
 * own. Normalising here, at the store boundary, is what makes the link
 * bidirectional for free: the grid, the species page, the completion counts, the
 * cascades and the sync all address one row, so neither side can be stale and
 * there is no second copy to keep in step.
 *
 * Everything else about a form dex stays its own: which cells it shows, its
 * pills, its filters, its wish-list keys. Only the catch state is shared,
 * because only the catch state is the same fact.
 */
export function storageVariantFor(variant: PokedexVariant): PokedexVariant {
  return isFormDexVariant(variant) ? "pokemon" : variant;
}

/**
 * Keep the SECONDARY form cells of a two-form species in lockstep with the base
 * cell (2026-07-24). Mega Charizard/Mewtwo/Raichu each show an X and a Y cell,
 * and G-Max Urshifu two styles — same dex number, separate store rows
 * (`mega:6` and `mega:6:charizard-mega-y`). The scanner reads only the base row,
 * so without this the second cell never fills, and a bulk region clear left it
 * behind. Per the owner's 2026-07-24 call we mirror the base: catching (or
 * clearing) the species moves BOTH cells together.
 *
 * Covers both dexes whose grid renders a species as two cells: Mega (Charizard/
 * Mewtwo/Raichu X&Y) and G-Max (Urshifu Single/Rapid). `dexExtraFormsFor` is the
 * shared source with the grid renderer, so a cell rendered is a cell this keeps
 * in sync. A no-op for every single-cell species and every other variant.
 * Mutates `store` and returns the rows it changed so the caller can enqueue them
 * for sync the same way it enqueues the base write. `state === "present"` writes
 * the form rows present; anything else clears them (matching a base clear).
 */
function cascadeMegaGmaxForms(
  store: StoreData,
  variant: PokedexVariant,
  dexNumber: number,
  state: CellState,
  source: DexEntryState["source"],
  now: number,
): Array<{ key: string; entry: DexEntryState }> {
  const changed: Array<{ key: string; entry: DexEntryState }> = [];
  for (const { formId } of dexExtraFormsFor(variant, dexNumber)) {
    const key = keyFor(variant, dexNumber, formId);
    if (state === "present") {
      const prev = store[key];
      if (prev && prev.state === "present") continue; // already caught
      const entry: DexEntryState = {
        variant, dexNumber, state: "present", source, updatedAt: now, formId,
      };
      store[key] = entry;
      changed.push({ key, entry });
    } else {
      // Clear: mirror the base tombstone so a re-scan starts clean and the
      // server drops the row. Skip when there is nothing to clear.
      if (!store[key]) continue;
      const entry: DexEntryState = {
        variant, dexNumber, state: "unknown", source: "manual", updatedAt: now, formId,
      };
      delete store[key];
      changed.push({ key, entry });
    }
  }
  return changed;
}

/**
 * Catching the species ⇒ its BASE form is caught too (2026-08-07).
 *
 * A species with a sub-dex has one entry that IS the plain species — `rattata`
 * alongside `rattata-alola`, `meowth` alongside `meowth-alola`/`meowth-galar`.
 * The base cell and that form row are the same animal, but nothing wrote the
 * form row, so the two disagreed. The existing form cascade only runs the other
 * way (any form caught ⇒ species caught); this is the missing direction.
 *
 * What it cost: /compare keys rows by (variant, speciesId, formId), so a friend
 * who happened to hold the base FORM row surfaced under "They have, you don't"
 * for a trainer who had the species ticked — the row read as a species they
 * already owned, because the panel doesn't render form names. Reported for
 * Gimmighoul on 2026-08-07.
 *
 * Deliberately narrow: it writes ONE mirror form — the species' own base-named
 * form when the group has one, else the group's FIRST form (2026-08-28 owner
 * direction: a species-level tick must never leave a form-tracked species
 * reading un-caught on every form-keyed surface, which is what kept a
 * hand-ticked Lucky in the friends' missing-lucky copy list). It never touches
 * the OTHER siblings: owning a Kanto Sandshrew says nothing about the Alolan
 * one, and those stay genuine trade leads.
 *
 * Same contract as the cascades around it: additive only, never downgrades an
 * existing present row, and mutates `store` while returning the rows it changed
 * so the caller enqueues them for server sync alongside the base write.
 */

/**
 * The one form row that mirrors a species-level cell. Base-named form first
 * ("sandshrew" for #27); for the ~59 groups where every form is a named
 * variant (Toxtricity Amped/Low-Key), the FIRST form stands in, so a
 * species-level statement always lands somewhere form-keyed surfaces can see.
 */
function mirrorFormIdFor(group: { speciesName: string; forms: ReadonlyArray<{ formId: string }> }): string | null {
  const base = group.forms.find((f) => f.formId === group.speciesName);
  return base?.formId ?? group.forms[0]?.formId ?? null;
}

/**
 * The mirror form for a species, for READ-side fallbacks (FormGrid): the form
 * cell a species-level tick vouches for, so the sub-dex can light it even when
 * an old write (pre-cascade, or the pre-fix trade path) never minted the
 * mirror row. Same computation as the write-side cascade, one source of truth.
 */
export function mirrorFormIdForSpecies(dexNumber: number): string | null {
  const group = FORM_GROUPS_BY_SPECIES_ID.get(dexNumber);
  return group ? mirrorFormIdFor(group) : null;
}

function cascadeBaseSpeciesForm(
  store: StoreData,
  variant: PokedexVariant,
  dexNumber: number,
  source: DexEntryState["source"],
  now: number,
): Array<{ key: string; entry: DexEntryState }> {
  // Mega/G-Max are independent cells per form, not a base-species axis — the
  // same exemption applyFormCascade makes.
  if (isMegaGmaxVariant(variant)) return [];
  const group = FORM_GROUPS_BY_SPECIES_ID.get(dexNumber);
  if (!group) return [];
  const mirrorFormId = mirrorFormIdFor(group);
  if (!mirrorFormId) return [];
  const key = keyFor(variant, dexNumber, mirrorFormId);
  const prev = store[key];
  if (prev && prev.state === "present") return [];
  const entry: DexEntryState = {
    variant,
    dexNumber,
    state: "present",
    source,
    updatedAt: now,
    formId: mirrorFormId,
  };
  store[key] = entry;
  return [{ key, entry }];
}

/**
 * The DOWNWARD half of cascadeBaseSpeciesForm (2026-08-11).
 *
 * `cascadeBaseSpeciesForm` mirrors a base-cell catch onto the species' own base
 * form row, and is documented "additive only, never downgrades". That made the
 * mirror one-directional: ticking a species wrote BOTH rows, unticking wrote
 * only the base one, and the mirror kept a stale `present` forever. Because the
 * two rows describe the SAME Pokémon — the premise the server's base-form fold
 * rests on — that stale row is not a harmless duplicate, it is a second opinion
 * that outlives the first. The Compare API filters to `state = 'present'` BEFORE
 * folding forms onto the base, so the corrected row is gone before the fold runs
 * and the mirror is promoted back into a base catch.
 *
 * So clearing a base cell now clears its mirror, with the SAME state the user
 * chose (missing vs unknown are different answers and both must carry through).
 *
 * Deliberately narrow, matching its sibling:
 *   - only the species' OWN base form ("tauros" for #128) — a regional or named
 *     sibling like "tauros-paldea-aqua" is a different Pokémon and a real trade
 *     lead, so it is never touched;
 *   - Mega/G-Max are exempt (independent cells per form, not a base-species axis);
 *   - a mirror the user MANUALLY set is left alone. Their explicit answer on the
 *     sub-dex cell outranks a cascade from the cell above it.
 *
 * Mutates `store` and returns the rows it changed, so the caller enqueues them
 * for server sync exactly as the additive path does.
 */
function clearBaseSpeciesFormMirror(
  store: StoreData,
  variant: PokedexVariant,
  dexNumber: number,
  state: DexEntryState["state"],
  source: DexEntryState["source"],
  now: number,
): Array<{ key: string; entry: DexEntryState }> {
  if (isMegaGmaxVariant(variant)) return [];
  const group = FORM_GROUPS_BY_SPECIES_ID.get(dexNumber);
  if (!group) return [];
  // The SAME computed mirror form as the additive half (base-named form, else
  // the group's first form), so tick and untick can never target two rows.
  const mirrorFormId = mirrorFormIdFor(group);
  if (!mirrorFormId) return [];
  const key = keyFor(variant, dexNumber, mirrorFormId);
  const prev = store[key];
  // Nothing mirrored here, or it already agrees — no write, no sync traffic.
  if (!prev || prev.state === state) return [];
  // An explicit hand-set answer on the form cell is the user's, not ours.
  if (prev.source === "manual" && source !== "manual") return [];
  const entry: DexEntryState = {
    variant,
    dexNumber,
    state,
    source,
    updatedAt: now,
    formId: mirrorFormId,
  };
  store[key] = entry;
  return [{ key, entry }];
}

/**
 * b17d — shiny-caught (or any cascading-variant catch) implies a MAIN catch.
 *
 * Catching a species in ANY form proves the species is caught, so a present
 * entry written under a cascading variant (shiny/lucky/xxl/xxs/perfect) must
 * also ensure the species is marked present in the MAIN ("pokemon") dex. This
 * is the WRITE-side companion to `composedMainSummary`'s read-time cascade:
 * the count was already cascade-aware, but per-entry MAIN reads
 * (`getDexEntryState("pokemon", n)`, which drives the Pokédex grid tiles and
 * any server-records projection) only see what is physically written. Without
 * this, a shiny-only scan lit the Shiny tab but left the MAIN grid showing
 * silhouettes for those same species.
 *
 * The rule is strictly ONE-DIRECTIONAL and never destructive:
 *   - cascading-variant present  ⇒ ensure MAIN present (this function).
 *   - MAIN present                ⇒ never implies any variant (no reverse).
 *   - a "missing"/"unknown" variant read never reaches here, so a shiny
 *     silhouette can never clear an existing MAIN catch.
 *
 * Last-write-wins: it never downgrades an already-present MAIN row (any
 * source), but a fresh catch DOES upgrade a `missing`/`unknown` MAIN row to
 * present even when the user had manually marked it not-caught. Returns true
 * only when it actually wrote a new MAIN present row.
 */
function ensureMainCaughtFromCascade(
  store: StoreData,
  variant: PokedexVariant,
  dexNumber: number,
): boolean {
  if (variant === "pokemon") return false;
  if (!CASCADING_VARIANTS.includes(variant)) return false;
  return writeMainPresentIfAbsent(store, dexNumber);
}

/**
 * Non-destructive MAIN ("pokemon") present write shared by every "a catch in
 * variant X implies the species is caught" linkage. Additive-only under
 * last-write-wins: it never downgrades an already-present MAIN row (any
 * source), but it DOES upgrade a `missing`/`unknown` row — including a manual
 * "not caught" — to present. Returns true only when it wrote a new MAIN present
 * row.
 */
// NOTE (2026-07-18): dex writes are LAST-WRITE-WINS. There is deliberately no
// source-precedence guard here any more — a hand edit and a scan are equally
// valid statements about a cell, and the most recent one is the truth. The old
// "manual always wins" rule let a STALE user row (notably the unknown/manual
// tombstone an untick writes) outrank fresh scan data forever, which is what
// made a cleared dex impossible to re-scan. See dexEntryResolver.

function writeMainPresentIfAbsent(
  store: StoreData,
  dexNumber: number,
): boolean {
  const mainKey = keyFor("pokemon", dexNumber);
  const prev = store[mainKey];
  // Last-write-wins: this cascade is additive-only. It never DOWNGRADES an
  // already-present MAIN row (regardless of source), but it DOES upgrade a
  // `missing`/`unknown` row to present even when that row was a manual edit —
  // a fresh catch is the newest statement and outranks an older "I don't have
  // it". (The old "a manual edit wins" guard here was removed 2026-07-18; it
  // was the source-precedence rule that made a cleared dex unscannable.)
  if (prev && prev.state === "present") return false;
  store[mainKey] = {
    variant: "pokemon",
    dexNumber,
    state: "present",
    source: "scan",
    updatedAt: Date.now(),
  };
  return true;
}

/**
 * Curated sub-dexes (Shadow / Purified) that, on a valid present write, also
 * tick the base Pokémon (All) dex for the same National Dex id. Unlike
 * [[CASCADING_VARIANTS]] (the read-time MAIN summary cascade), these are curated
 * availability rosters: only ids that are genuine members of the variant's
 * curated roster may exist there at all, so the linkage re-checks
 * `validateDexMember` before touching MAIN. This keeps the rule strictly
 * one-directional and never broadens Shadow/Purified eligibility — Pokemon All
 * membership must never imply Shadow/Purified.
 */
export const CURATED_MAIN_LINKED_VARIANTS: readonly PokedexVariant[] = [
  "shadow",
  "purified",
];

/**
 * Shadow/Purified → MAIN linkage. A successful (in-roster) present write for a
 * Shadow or Purified species proves the species is caught, so it must also mark
 * the base Pokémon (All) dex present for the same dex id.
 *
 * Strictly gated so a blocked/out-of-roster observation can never tick MAIN:
 *   - returns false for any variant that is not Shadow/Purified;
 *   - returns false when `(variant, dexId)` is not in the curated roster, so an
 *     invalid Shadow/Purified write cannot leak into MAIN through this path;
 *   - never downgrades a present MAIN row, but a fresh catch does upgrade a
 *     `missing`/`unknown` MAIN row (including a manual not-caught) to present,
 *     per last-write-wins.
 *
 * Returns true only when it actually wrote a new MAIN present row.
 */
function ensureMainCaughtFromCuratedRoster(
  store: StoreData,
  variant: PokedexVariant,
  dexNumber: number,
): boolean {
  if (!CURATED_MAIN_LINKED_VARIANTS.includes(variant)) return false;
  if (!validateDexMember(variant, dexNumber).valid) return false;
  return writeMainPresentIfAbsent(store, dexNumber);
}

/** True for any variant whose present write links to the MAIN dex. */
function isMainLinkedVariant(variant: PokedexVariant): boolean {
  return (
    CASCADING_VARIANTS.includes(variant) ||
    CURATED_MAIN_LINKED_VARIANTS.includes(variant)
  );
}

/**
 * PR-E2c-cascade: apply form-level cascade writes to the in-memory
 * store and enqueue each upgraded row for server sync.
 *
 * Returns the keys that were touched so callers can emit a single
 * `DEX_STATE_EVENT` after persistence (the existing emit-once-per-write
 * pattern in this file).
 *
 * Design notes:
 *   - Pure work happens in `computeFormCascade` (testable in isolation).
 *   - We pass a readEntry hook bound to the live `store` so the
 *     cascade sees prior in-batch writes (e.g. when a single setManual
 *     call triggers form fanout + parent flag; the parent flag check
 *     observes the upgraded form rows too -- though they don't gate
 *     the parent flag, this keeps the engine future-safe).
 *   - We never reach here for state === "missing" (callers gate on
 *     state). computeFormCascade also no-ops on "missing" as a defence.
 */
function applyFormCascade(
  store: StoreData,
  sourceVariant: PokedexVariant,
  dexNumber: number,
  formId: string,
  state: CellState,
): string[] {
  const writes = computeFormCascade({
    sourceVariant,
    dexNumber,
    formId,
    state,
    readEntry: (v, n, fid) => store[keyFor(v, n, fid)],
  });
  const touched: string[] = [];
  for (const w of writes) {
    store[w.key] = w.entry;
    touched.push(w.key);
    enqueuePending(w.key, w.entry);
  }
  return touched;
}

/**
 * Perf (dex-performance-lag): in-memory cache of the parsed store.
 *
 * Before this cache, `safeReadStore` ran a full `JSON.parse` of the entire
 * localStorage blob on EVERY call — and the Pokédex grid calls
 * `getDexEntryState` (→ `safeReadStore`) once per visible cell (~1,025), plus
 * every headline/completion scan re-parses too. With a large dex that was the
 * dominant source of the "laggy once a lot of data goes in" report: thousands
 * of full JSON parses per render.
 *
 * The cache holds the last parsed object. It is kept in lockstep with
 * localStorage: `safeWriteStore` updates it on a successful write and drops it
 * on a failed write (so the next read re-parses the un-mutated truth), the
 * reset helpers null it after a direct `removeItem`, and a cross-tab `storage`
 * event drops it so another tab's write is still picked up.
 */
let storeCache: StoreData | null = null;

/** Drop the parsed-store cache so the next read re-parses localStorage. */
function invalidateStoreCache(): void {
  storeCache = null;
}

function safeReadStore(): StoreData {
  if (storeCache) return storeCache;
  if (typeof localStorage === "undefined") return {};
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      storeCache = {};
      return storeCache;
    }
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object") {
      storeCache = parsed as StoreData;
      return storeCache;
    }
  } catch {
    /* fall through */
  }
  storeCache = {};
  return storeCache;
}

/**
 * Persist the store to localStorage. Returns true when the write landed,
 * false when it threw (quota, private-mode/iOS WKWebView blocked storage) or
 * when there is no localStorage at all.
 *
 * PiP-trust fix: callers that report a committed result to the user (the live
 * scan / PiP "Added" path via applyScanObservations) MUST honour this boolean.
 * A swallowed failure here is exactly how the PiP could claim success while
 * nothing persisted on iOS WKWebView.
 */
/**
 * Coalesced writes during a LIVE SCAN (2026-09-22, Galaxy S21+ scan speed).
 *
 * applyScanObservations used to JSON.stringify the WHOLE multi-variant dex
 * and localStorage.setItem it synchronously on the main thread on every
 * frame that landed anything, inside the window the scanner has to finish a
 * frame in. While a scan is running (the hook flips this on at start and
 * off at stop/finish, which also flushes), the first write of a burst goes
 * through durably as before (so blocked storage is still caught on the spot)
 * and further writes within SCAN_WRITE_COALESCE_MS update the in-memory
 * cache (authoritative for every read) and schedule ONE trailing write.
 * pagehide / hidden flush it, so backgrounding never loses a frame.
 */
const SCAN_WRITE_COALESCE_MS = 800;
let scanWriteCoalescing = false;
let lastDurableWriteAt = 0;
let pendingWrite: StoreData | null = null;
let pendingWriteTimer: ReturnType<typeof setTimeout> | null = null;

export function flushPendingDexWrite(): void {
  if (pendingWriteTimer != null) {
    clearTimeout(pendingWriteTimer);
    pendingWriteTimer = null;
  }
  if (pendingWrite) {
    const data = pendingWrite;
    pendingWrite = null;
    safeWriteStore(data);
    lastDurableWriteAt = Date.now();
  }
}

/** The live scan turns coalescing on at start and off (with a flush) at stop. */
export function setScanWriteCoalescing(on: boolean): void {
  scanWriteCoalescing = on;
  if (!on) flushPendingDexWrite();
}

if (typeof window !== "undefined") {
  window.addEventListener("pagehide", flushPendingDexWrite);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flushPendingDexWrite();
  });
}

function safeWriteStoreCoalesced(data: StoreData): boolean {
  if (!scanWriteCoalescing || typeof localStorage === "undefined") return safeWriteStore(data);
  const now = Date.now();
  if (lastDurableWriteAt > 0 && now - lastDurableWriteAt < SCAN_WRITE_COALESCE_MS) {
    storeCache = data;
    pendingWrite = data;
    if (pendingWriteTimer == null) {
      pendingWriteTimer = setTimeout(() => {
        pendingWriteTimer = null;
        flushPendingDexWrite();
      }, SCAN_WRITE_COALESCE_MS);
    }
    return true;
  }
  const ok = safeWriteStore(data);
  if (ok) lastDurableWriteAt = now;
  return ok;
}

function safeWriteStore(data: StoreData): boolean {
  if (typeof localStorage === "undefined") {
    // No durable store, but keep the in-memory cache coherent so reads within
    // this session still see the mutation (SSR / test-without-localStorage).
    storeCache = data;
    return false;
  }
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    // Keep the cache pointing at exactly what we persisted.
    storeCache = data;
    return true;
  } catch {
    // quota / blocked (iOS WKWebView private-mode or storage disabled). We can't
    // persist durably, but KEEP the just-mutated object as the in-memory cache so
    // a scan/edit the user just confirmed still shows for THIS session — and a
    // later successful write re-persists the whole cache, recovering it. We used
    // to drop the cache here, which silently discarded a confirmed scan the
    // instant a storage write failed (the "OCR read it, toast said added, but the
    // dex never updated" bug on device). Still return false so callers know the
    // write wasn't durable.
    storeCache = data;
    return false;
  }
}

function emitChange(): void {
  // Bump the store version FIRST (unconditionally, before the window guard) so
  // the derived-dex memo below invalidates even in non-DOM contexts (tests,
  // SSR). Every store mutation routes through emitChange, so this is the single
  // invalidation chokepoint for anything computed from the store.
  storeVersion += 1;
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(new Event(DEX_STATE_EVENT));
  } catch {
    /* swallow */
  }
}

// Perf (dex-performance-lag): another tab writing the dex state must still be
// picked up here. Before the parse cache every read re-parsed localStorage, so
// a cross-tab write surfaced on the next render for free; with the cache we
// must explicitly drop it and signal a change when the shared key mutates.
if (typeof window !== "undefined") {
  try {
    window.addEventListener("storage", (e) => {
      if (e.key === STORAGE_KEY || e.key === null) {
        invalidateStoreCache();
        // A cross-tab change also invalidates the headline/completion totals,
        // so refresh the snapshot the same way an in-tab checkpoint would.
        commitDexTotals();
        emitChange();
      }
    });
  } catch {
    /* addEventListener unavailable — cross-tab sync simply falls back to
       whatever the next in-tab mutation refreshes. */
  }
}

// ── Headline / completion totals snapshot (dex-performance-lag) ─────────────
//
// The Pokédex headline "caught" count and the two completion rings derive from
// `composedVariantSummary`, an O(store) cascade scan. They used to recompute on
// EVERY `DEX_STATE_EVENT` — i.e. on every scan-frame write and every manual
// toggle — which, on a large dex, meant a full-store scan per mutation while the
// grid was also re-rendering.
//
// Per user direction the totals now recompute only at lifecycle checkpoints:
// app open, manual-edit toggle off, live-scan broadcast close, and screenshot
// finish. Between checkpoints `composedVariantSummarySnapshot` returns the last
// snapshot, so a burst of writes costs zero extra scans. The per-cell grid state
// (`getDexEntryState`) stays live off `DEX_STATE_EVENT`, so a tapped cell still
// flips immediately during manual edit — only the aggregate totals are deferred.

/** Fires when the totals snapshot is refreshed (a checkpoint), not per mutation. */
export const DEX_TOTALS_EVENT = "nexus:dex-totals-changed";

let dexTotalsVersion = 0;
const totalsSnapshotCache = new Map<PokedexVariant, VariantCountSummary>();
// Present-id sets memoised on the SAME checkpoint cadence as the summary cache,
// so the "released caught" completion count (present ∩ released roster) is a
// single O(store) scan per variant per checkpoint, not per render.
const presentIdsSnapshotCache = new Map<PokedexVariant, Set<number>>();


/**
 * Cascade-composed variant summary, memoised until the next totals checkpoint.
 * Returns the cached snapshot between checkpoints (a stable object reference, so
 * downstream `useMemo`/`useSyncExternalStore` deps stay put); recomputes at most
 * once per variant per checkpoint. Switching the viewed variant computes and
 * caches that variant on demand.
 */
export function composedVariantSummarySnapshot(
  variant: PokedexVariant,
): VariantCountSummary {
  const cached = totalsSnapshotCache.get(variant);
  if (cached) return cached;
  const fresh = composedVariantSummary(variant);
  totalsSnapshotCache.set(variant, fresh);
  return fresh;
}

/**
 * Recompute the Dex headline / completion totals from current store state.
 *
 * Call this ONLY at the lifecycle checkpoints (app open, manual-edit toggle off,
 * live-scan broadcast close, screenshot finish) — never on every mutation. It
 * clears the snapshot cache, bumps the version, and fires `DEX_TOTALS_EVENT` so
 * subscribers (the headline count + completion rings) re-read the fresh numbers.
 */
export function commitDexTotals(): void {
  dexTotalsVersion += 1;
  totalsSnapshotCache.clear();
  presentIdsSnapshotCache.clear();
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(new Event(DEX_TOTALS_EVENT));
  } catch {
    /* swallow */
  }
}

// ── Derived "Complete" dex (pokedex-complete-dex) ──────────────────────────
//
// The Complete dex is computed from the other dexes, never stored. Deriving it
// per grid cell would rescan the store 1,025× per render, so we memoise the
// present-set and rebuild it only when (a) the store mutates (storeVersion) or
// (b) the Shadow/Purified rosters change (rosterSignature) — the two inputs the
// derivation reads.
let storeVersion = 0;
/** Bumps on every store mutation (emitChange); a cheap cache key for per-frame derivations. */
export function getDexStoreVersion(): number {
  return storeVersion;
}
let completeCacheVersion = -1;
let completeCacheRosterSig = "";
let completeCacheSet: Set<number> = new Set();

/**
 * Cheap fingerprint of the roster inputs. The runtime Shadow roster only ever
 * grows (setRuntimeShadowRoster unions ids on), so length is a sufficient
 * change signal; Purified is static but included for symmetry/future-proofing.
 */
function completeRosterSignature(): string {
  const shadow = curatedRosterFor("shadow") ?? [];
  const purified = curatedRosterFor("purified") ?? [];
  return `${shadow.length}:${purified.length}`;
}

/** Recompute the present-by-variant snapshot + complete set from one store scan. */
function recomputeCompleteSet(): Set<number> {
  const store = safeReadStore();
  const presentByVariant = new Map<PokedexVariant, Set<number>>();
  const contributing = new Set<PokedexVariant>([
    ...COMPLETE_BASE_VARIANTS,
    ...COMPLETE_CONDITIONAL_VARIANTS,
  ]);
  for (const k in store) {
    const e = store[k];
    if (e.formId) continue; // base-species rows only, never per-form catches
    if (e.state !== "present") continue;
    if (!contributing.has(e.variant)) continue;
    let set = presentByVariant.get(e.variant);
    if (!set) {
      set = new Set<number>();
      presentByVariant.set(e.variant, set);
    }
    set.add(e.dexNumber);
  }
  const shadowRoster = new Set(curatedRosterFor("shadow") ?? []);
  const purifiedRoster = new Set(curatedRosterFor("purified") ?? []);
  return computeCompletePresentSet({
    presentByVariant,
    hasShadow: (n) => shadowRoster.has(n),
    hasPurified: (n) => purifiedRoster.has(n),
  });
}

/** The memoised set of dex numbers that are currently "complete". */
function completePresentSet(): Set<number> {
  const sig = completeRosterSignature();
  if (completeCacheVersion === storeVersion && completeCacheRosterSig === sig) {
    return completeCacheSet;
  }
  completeCacheSet = recomputeCompleteSet();
  completeCacheVersion = storeVersion;
  completeCacheRosterSig = sig;
  return completeCacheSet;
}

/** Synthesised present entry for a derived-dex cell (no source badge). */
function derivedCompleteEntry(dexNumber: number): DexEntryState {
  return {
    variant: "complete",
    dexNumber,
    state: "present",
    source: "scan",
    updatedAt: 0,
  };
}

// ── Last-active variant (b17p B1) ──────────────────────────────────────────

/**
 * Record the dex variant pill the user is currently viewing. Called by the
 * Pokedex page whenever its active variant changes. Cached in-module and
 * persisted to localStorage; never throws.
 */
export function setLastActiveVariant(variant: PokedexVariant): void {
  const changed = lastActiveVariantCache !== variant;
  lastActiveVariantCache = variant;
  if (typeof localStorage !== "undefined") {
    try {
      localStorage.setItem(LAST_VARIANT_KEY, variant);
    } catch {
      /* quota / blocked — in-memory cache still holds it for this session */
    }
  }
  // b17r R2: notify subscribers (useLastActiveVariant) so surfaces like the
  // hero live-scan button re-resolve their scan variant when the user switches
  // pills, instead of reading a stale cold-launch snapshot. Only emit on a real
  // change so we do not churn unrelated DEX_STATE_EVENT listeners.
  if (changed) emitChange();
}

/**
 * Cold-launch landing default. Force the Pokédex to open on the Main ("pokemon")
 * dex on every fresh app start, regardless of the variant the user last viewed in
 * a previous session, so the page that preloads on launch is the one the trainer
 * lands on. Called once at boot (main.tsx) BEFORE React renders, so the Pokédex
 * page's initial getLastActiveVariant() read already returns "pokemon". Within a
 * session, setLastActiveVariant still tracks the user's pill selection normally
 * (and the Pokédex↔SubDex sync is unaffected). Does NOT emit — there are no
 * subscribers this early, and it must not churn DEX_STATE_EVENT listeners.
 */
export function resetLandingVariantToMain(): void {
  lastActiveVariantCache = "pokemon";
  if (typeof localStorage !== "undefined") {
    try {
      localStorage.setItem(LAST_VARIANT_KEY, "pokemon");
    } catch {
      /* quota / blocked — the in-memory cache still forces Main this launch */
    }
  }
}

/**
 * Read the last-active variant, or null when the user has not selected one this
 * session and none is persisted. Callers fall back to their own default.
 */
export function getLastActiveVariant(): PokedexVariant | null {
  if (lastActiveVariantCache !== null) return lastActiveVariantCache;
  if (typeof localStorage === "undefined") return null;
  try {
    const raw = localStorage.getItem(LAST_VARIANT_KEY);
    if (raw) {
      lastActiveVariantCache = raw as PokedexVariant;
      return lastActiveVariantCache;
    }
  } catch {
    /* fall through */
  }
  return null;
}

/**
 * b17r R2: React hook that reactively tracks the last-active variant. Components
 * (the hero live-scan button) subscribe so they re-render — and re-resolve the
 * variant they scan for — the moment the user switches dex pills, rather than
 * reading the stale snapshot captured at cold launch.
 *
 * Backed by `useSyncExternalStore` over the existing `DEX_STATE_EVENT` pub/sub.
 * The client snapshot is the module cache (synchronous, test-safe). The server
 * snapshot returns null so SSR renders the caller's own default.
 */
function subscribeLastActiveVariant(onChange: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  window.addEventListener(DEX_STATE_EVENT, onChange);
  return () => window.removeEventListener(DEX_STATE_EVENT, onChange);
}

export function useLastActiveVariant(): PokedexVariant | null {
  return useSyncExternalStore(
    subscribeLastActiveVariant,
    getLastActiveVariant,
    () => null,
  );
}

// ── Per-cell live state subscription ─────────────────────────────────────────
// Each Pokédex grid cell subscribes to JUST its own (variant, dexNumber, formId)
// slice, so a scan/toggle write re-renders only the cells that actually changed
// — not the whole 1025-cell page. Before this, the page held one render-tick that
// re-ran the entire grid function on every DEX_STATE_EVENT; a scan burst then
// re-ran the page (and reconciled 1025 memoised cells) per observation. Moving
// the read into the cell means "flick back after a scan and watch the entries you
// just caught light up" costs one cell re-render each, with no page-wide pass.
//
// The snapshot is a PACKED INTEGER (present|manual|trade in bits 0..2) so
// useSyncExternalStore compares by value: a cell only re-renders when its own
// three flags change. getSnapshot is O(1) (cached store parse + key lookup),
// so the ~1025 snapshot reads per event stay cheap; React re-renders just the
// cells whose packed value moved.
export function subscribeDexState(onChange: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  window.addEventListener(DEX_STATE_EVENT, onChange);
  return () => window.removeEventListener(DEX_STATE_EVENT, onChange);
}

function packDexEntryState(
  variant: PokedexVariant,
  dexNumber: number,
  formId?: string | null,
): number {
  const e = getDexEntryState(variant, dexNumber, formId);
  return (
    (e?.state === "present" ? 1 : 0) |
    (e?.source === "manual" ? 2 : 0) |
    (e?.source === "trade" ? 4 : 0)
  );
}

/**
 * Reactively read a single cell's (present, manual, trade) flags. Re-renders the
 * calling component only when THAT cell's own state changes.
 */
export function useDexEntryCellState(
  variant: PokedexVariant,
  dexNumber: number,
  formId?: string | null,
): { present: boolean; manual: boolean; trade: boolean } {
  const packed = useSyncExternalStore(
    subscribeDexState,
    () => packDexEntryState(variant, dexNumber, formId),
    () => 0,
  );
  return {
    present: (packed & 1) !== 0,
    manual: (packed & 2) !== 0,
    trade: (packed & 4) !== 0,
  };
}

// ── Read API ─────────────────────────────────────────────────────────────

export function getDexEntryState(
  variant: PokedexVariant,
  dexNumber: number,
  formId?: string | null,
): DexEntryState | null {
  // pokedex-complete-dex: the Complete dex is derived, not stored. A species is
  // "present" iff it clears every applicable dex; there are no form rows and no
  // missing rows to read.
  if (variant === "complete") {
    return completePresentSet().has(dexNumber)
      ? derivedCompleteEntry(dexNumber)
      : null;
  }
  // A Regional / Costume cell reads MAIN's row for the same form — see
  // storageVariantFor.
  return safeReadStore()[keyFor(storageVariantFor(variant), dexNumber, formId)] ?? null;
}

/**
 * List every stored entry for a variant. Returns BOTH base-species rows
 * and form-level rows (PR-E2c-store) by default. The legacy completion
 * counters that drive the dex pills must NOT double-count form rows
 * against the species total, so they should pass {includeForms: false}
 * (the default), which filters out rows with a non-empty formId. The
 * sub-dex screens (PR-E2c-ui) pass {includeForms: true} to render the
 * form grid.
 */
export function listDexEntriesForVariant(
  variant: PokedexVariant,
  opts?: { includeForms?: boolean },
): DexEntryState[] {
  // pokedex-complete-dex: derived present-only rows (no stored/form/missing
  // rows). This feeds the grid's status filter + the caught-count summary.
  if (variant === "complete") {
    return Array.from(completePresentSet())
      .sort((a, b) => a - b)
      .map((n) => derivedCompleteEntry(n));
  }
  const includeForms = opts?.includeForms ?? false;
  const store = safeReadStore();
  const out: DexEntryState[] = [];
  for (const k in store) {
    if (!k.startsWith(`${variant}:`)) continue;
    const e = store[k];
    if (!includeForms && e.formId) continue;
    out.push(e);
  }
  out.sort((a, b) => a.dexNumber - b.dexNumber);
  return out;
}

/**
 * The most recently ADDED catches, newest first, across every dex.
 *
 * "Added" is `state === "present"` ordered by `updatedAt`, so it covers all
 * three ways a row appears — a live scan, a completed trade, or a manual tick —
 * which is exactly the mix Compare's recents panel wants to show. `source` rides
 * along so the panel can say which it was.
 *
 * De-duplicated by SPECIES, keeping the newest row. Catching a Pikachu that
 * lands as both a Shiny row and a cascaded MAIN row is one event to a reader,
 * and showing it twice would waste half a three-slot panel.
 *
 * The "complete" variant is excluded: it is a derived rollup with no real
 * timestamp of its own, so it would stamp every species with the same instant.
 */
export function listRecentCatches(limit = 3): DexEntryState[] {
  const store = safeReadStore();
  const newestBySpecies = new Map<number, DexEntryState>();
  for (const k in store) {
    const e = store[k];
    if (!e || e.state !== "present") continue;
    if (e.variant === "complete") continue;
    if (typeof e.updatedAt !== "number" || e.updatedAt <= 0) continue;
    const prev = newestBySpecies.get(e.dexNumber);
    if (!prev || e.updatedAt > prev.updatedAt) newestBySpecies.set(e.dexNumber, e);
  }
  // Tie-break on dex number, because ties are the NORMAL case here, not the
  // edge one: a scan commits its whole batch inside a millisecond, so a bulk
  // catch leaves dozens of rows with an identical `updatedAt`. Without a
  // second key the order falls back to store iteration and the panel can
  // reshuffle between renders for no reason the user can see. Which three of
  // an indistinguishable batch you show is arbitrary; that it is the SAME
  // three each time is not.
  return Array.from(newestBySpecies.values())
    .sort((a, b) => b.updatedAt - a.updatedAt || b.dexNumber - a.dexNumber)
    .slice(0, Math.max(0, limit));
}


export interface VariantCountSummary {
  present: number;
  missing: number;
  /** Total entries with any recorded state (scan or manual). */
  recorded: number;
}

export function variantCountSummary(variant: PokedexVariant): VariantCountSummary {
  const entries = listDexEntriesForVariant(variant);
  let present = 0;
  let missing = 0;
  for (const e of entries) {
    if (e.state === "present") present += 1;
    else if (e.state === "missing") missing += 1;
  }
  return { present, missing, recorded: entries.length };
}

/**
 * PR-C Problem 2 (client mirror of the server cascade composer):
 * the variants that cascade into the MAIN ("pokemon") dex. A species present
 * in ANY of these implies a MAIN presence, because Shiny/Lucky/XXL/XXS/Perfect
 * are properties of an existing Pokémon, not separate creatures.
 */
export const CASCADING_VARIANTS: readonly PokedexVariant[] = [
  "shiny",
  "lucky",
  "xxl",
  "xxs",
  "perfect",
];

/**
 * MAIN-dex summary with cascade applied. A species counts as MAIN-present if it
 * is marked present in the `pokemon` variant OR present in any cascading
 * variant. A "missing" mark in MAIN is only honoured for species that have no
 * present variant entry — a surviving variant overrides marked-missing.
 *
 * This is the device-local mirror of `composeUserDexState` in
 * cloudflare/src/lib/dexCompose.ts; both must apply the same rule so the
 * on-screen Pokémon Dex card matches the server-derived count.
 */
/**
 * The Main (pokemon) present + missing species SETS. A cascading-variant catch
 * (shiny/lucky/xxl/xxs/perfect present - see CASCADING_VARIANTS; mega/gmax are
 * deliberately NOT in it, a Mega catch does not cascade to MAIN) implies the
 * base species is caught, and variant
 * presence wins over a Main marked-missing. Shared by composedMainSummary (which
 * needs the sizes) and composedVariantPresentIds (which needs the present set to
 * intersect against a roster for the "released caught" completion count).
 */
function mainPresentMissingSets(): { present: Set<number>; missing: Set<number> } {
  const store = safeReadStore();
  const present = new Set<number>();
  const missing = new Set<number>();
  // The NEWEST cascading-variant catch per species, so an inferred presence can
  // be weighed against an explicit MAIN mark by recency rather than always
  // beating it. See the untick rule below.
  const cascadeAt = new Map<number, number>();
  // When the trainer last said "I do NOT have this" on the MAIN dex. There are
  // three ways that statement is recorded, and all of them count:
  //
  //   1. a MAIN row with state 'missing' — the per-cell untick
  //   2. a MAIN row with state 'unknown' AND source 'manual' — the tombstone
  //      clearManual writes, reaching the local store when pulled back from the
  //      server. The source check matters: per clearManual's own contract,
  //      unknown+manual means "user explicitly cleared this" as opposed to
  //      "device never saw it". An 'unknown' from anywhere else is genuine
  //      uncertainty (the OCR returns it at zero confidence) and asserts
  //      nothing, so it must not suppress a real catch.
  //   3. a per-entry reconciliation tombstone — the durable record of a
  //      removal. This is the only evidence that survives clearManual and the
  //      Quick-update matrix, both of which DELETE the local row outright,
  //      leaving nothing in the store to read
  //
  // Missing any one of these would let the cascade quietly re-assert a catch
  // the trainer had removed, which is the bug this whole rule exists to stop.
  const mainNotCaughtAt = new Map<number, number>();
  const noteNotCaught = (dexNumber: number, at: number) => {
    mainNotCaughtAt.set(dexNumber, Math.max(mainNotCaughtAt.get(dexNumber) ?? 0, at));
  };

  for (const k in store) {
    const e = store[k];
    if (e.variant === "pokemon") {
      if (e.state === "present") present.add(e.dexNumber);
      else if (e.state === "missing") {
        missing.add(e.dexNumber);
        noteNotCaught(e.dexNumber, e.updatedAt ?? 0);
      } else if (e.state === "unknown" && e.source === "manual") {
        noteNotCaught(e.dexNumber, e.updatedAt ?? 0);
      }
    } else if (CASCADING_VARIANTS.includes(e.variant) && e.state === "present") {
      cascadeAt.set(e.dexNumber, Math.max(cascadeAt.get(e.dexNumber) ?? 0, e.updatedAt ?? 0));
    }
  }

  // Source 3: removals whose row no longer exists locally.
  const tombstones = readTombstones();
  for (const key in tombstones.entries) {
    const sep = key.indexOf(":");
    if (sep < 0 || key.slice(0, sep) !== "pokemon") continue;
    const dexNumber = Number(key.slice(sep + 1));
    if (!Number.isFinite(dexNumber)) continue;
    noteNotCaught(dexNumber, tombstones.entries[key] ?? 0);
  }

  // A cascading catch implies the base species is caught — EXCEPT where the
  // trainer has since said otherwise.
  //
  // This used to be unconditional ("variant presence wins over a Main marked
  // missing"), which quietly overrode a manual untick: the grid draws the MAIN
  // cell from the physically-written row and showed the silhouette the trainer
  // asked for, while this count inferred a catch and dropped it from `missing`.
  // The two then disagreed — a dex reading "948 / 951, 3 missing" with FOUR
  // silhouettes on screen. The trainer is the keeper of their dex, so an
  // explicit "not caught" must not be undone by an older inference.
  //
  // Resolved by recency, matching the last-write-wins rule the rest of the dex
  // uses: the cascade only asserts presence when its catch is NEWER than the
  // MAIN missing mark. In practice an untick is always the later statement,
  // because the WRITE-side cascade (ensureMainCaughtFromCascade) already set
  // MAIN present at the moment of the catch — so a MAIN 'missing' row can only
  // have been written afterwards.
  // STRICTLY newer, so a tie falls to the catch. Ties are ordinary here — the
  // store stamps in whole milliseconds, and an untick that cascades to sibling
  // dexes writes several rows on the same tick. Resolving a tie to "missing"
  // would resurrect the bug this rule exists to prevent, where clearing a cell
  // in order to re-catch it left the species stuck uncaught. Same convention as
  // the sync resolver: ties go to the catch.
  for (const [dexNumber, at] of cascadeAt) {
    const notCaughtAt = mainNotCaughtAt.get(dexNumber);
    if (notCaughtAt != null && notCaughtAt > at) continue; // untick is newer — honour it
    present.add(dexNumber);
  }

  present.forEach((n) => missing.delete(n));
  return { present, missing };
}

export function composedMainSummary(): VariantCountSummary {
  const { present, missing } = mainPresentMissingSets();
  const recorded = new Set<number>([...present, ...missing]);
  return { present: present.size, missing: missing.size, recorded: recorded.size };
}

/**
 * The set of species currently PRESENT (caught) for a variant. Main folds in the
 * cascading variants; every other variant is its own recorded present rows. Used
 * by the completion ring to count only CAUGHT-AND-RELEASED species (present ∩
 * released roster) so a caught species outside the released roster can't push the
 * "Pokémon GO Released" ring over 100% (device: 956/951).
 */
export function composedVariantPresentIds(variant: PokedexVariant): Set<number> {
  if (variant === "pokemon") return mainPresentMissingSets().present;
  const ids = new Set<number>();
  for (const e of listDexEntriesForVariant(variant)) {
    if (e.state === "present") ids.add(e.dexNumber);
  }
  return ids;
}

/**
 * Checkpoint-memoised `composedVariantPresentIds` — returns the cached present
 * set between totals checkpoints (cleared in `commitDexTotals`), so the
 * completion ring's "released caught" intersection stays O(1) on unrelated
 * re-renders, mirroring `composedVariantSummarySnapshot`.
 */
export function composedVariantPresentIdsSnapshot(
  variant: PokedexVariant,
): Set<number> {
  const cached = presentIdsSnapshotCache.get(variant);
  if (cached) return cached;
  const fresh = composedVariantPresentIds(variant);
  presentIdsSnapshotCache.set(variant, fresh);
  return fresh;
}

/**
 * Build 6 / v1.4.6 — per-filter summary for the Pokédex page headline.
 *
 * The "Pokémon" (MAIN) filter aggregates the UNION of every species the user
 * has caught across all variants: a variant catch IS a main-dex catch (an XXL
 * Tauros caught means Tauros is caught). So the MAIN pill routes through the
 * cascade (`composedMainSummary`), exactly like the server's
 * `composeUserDexState`.
 *
 * Build 5's PR #158 isolated the MAIN filter from the cascade — that was a
 * misread of the original direction and is reverted here.
 *
 * Variant-specific pills (Lucky, Shiny, XXL, XXS, Perfect) stay isolated to
 * their own variant: Lucky shows only Lucky catches, never Shiny's. They do not
 * aggregate, so they fall back to the plain per-variant summary.
 */
export function composedVariantSummary(variant: PokedexVariant): VariantCountSummary {
  if (variant === "pokemon") return composedMainSummary();
  // form-dexes: every cell is a FORM row, so the base-row count (which excludes
  // form rows) would read 0 no matter how much of the dex is filled. Count the
  // roster's form rows instead.
  if (isFormDexVariant(variant)) return formDexSummary(variant);
  const base = variantCountSummary(variant);
  // pokedex-mega-xy: the Mega dex (Charizard/Mewtwo/Raichu Y) and the G-Max dex
  // (Urshifu Rapid) have secondary form cells keyed by formId, which the
  // base-row count excludes. Add their present rows so the caught count matches
  // the grid + roster total.
  if (variant === "mega" || variant === "gmax") {
    const extra = extraFormCellsPresent(variant);
    return {
      present: base.present + extra,
      missing: base.missing,
      recorded: base.recorded + extra,
    };
  }
  return base;
}

/**
 * Present / missing / recorded across a form dex's cells. Counted over the
 * ROSTER rather than by scanning the store, so a stale row for a form that has
 * since left the registry cannot inflate the count past the grid's cell total.
 */
function formDexSummary(variant: PokedexVariant): VariantCountSummary {
  const store = safeReadStore();
  let present = 0;
  let missing = 0;
  const stored = storageVariantFor(variant);
  for (const cell of formDexCells(variant)) {
    const state = store[keyFor(stored, cell.dexNumber, cell.formId)]?.state;
    if (state === "present") present += 1;
    else if (state === "missing") missing += 1;
  }
  return { present, missing, recorded: present + missing };
}

/** Count of secondary Mega/G-Max form cells (X/Y, Single/Rapid) currently caught. */
function extraFormCellsPresent(variant: "mega" | "gmax"): number {
  const store = safeReadStore();
  const forms = variant === "mega" ? MEGA_EXTRA_FORMS : GMAX_EXTRA_FORMS;
  let count = 0;
  for (const f of forms) {
    if (store[keyFor(variant, f.baseId, f.formId)]?.state === "present") count += 1;
  }
  return count;
}

// ── Scan write recorder ──────────────────────────────────────────────────

/**
 * What a scan write did to the trainer's collection, from the trainer's point
 * of view. This is deliberately NARROWER than "the store wrote a row":
 *
 *   - `added`   — the cell was not present and now is. A re-observation of a
 *                 cell that was already present is not an add, even though the
 *                 store technically rewrites it to change provenance.
 *   - `removed` — the cell WAS present and now is not (the self-correction
 *                 ratchet, or a scan reading a silhouette over an old catch).
 *                 A "missing" read of a cell that was never present is just an
 *                 observation and is not recorded at all.
 *
 * Anything else — a blocked write, an identical re-read, a derived variant — is
 * silent. The log is a record of what CHANGED, so a scan that read 300 cells
 * and changed four shows four rows.
 */
export interface ScanWriteRecord {
  variant: PokedexVariant;
  dexNumber: number;
  formId: string | null;
  action: "added" | "removed";
  /**
   * True when this write also lit the MAIN row for the species through the
   * cascade (a shiny catch implying the species is caught). Undo needs to know:
   * retracting the shiny row alone would leave an orphaned MAIN catch that the
   * scan, not the trainer, created.
   */
  cascadedMain: boolean;
}

export type ScanWriteRecorder = (record: ScanWriteRecord) => void;

let scanWriteRecorder: ScanWriteRecorder | null = null;

/**
 * Install (or clear, with null) the sink that receives every collection-changing
 * scan write. Owned by scanSessionStore, which installs a recorder for the
 * lifetime of one scan session and removes it on close — so writes outside a
 * scan are never attributed to a session, and this module never has to know
 * that a session log exists.
 */
export function setScanWriteRecorder(recorder: ScanWriteRecorder | null): void {
  scanWriteRecorder = recorder;
}

function recordScanWrite(record: ScanWriteRecord): void {
  const sink = scanWriteRecorder;
  if (!sink) return;
  try {
    sink(record);
  } catch {
    /* a broken log must never break a dex write. */
  }
}

// ── Write API ────────────────────────────────────────────────────────────

/**
 * Apply a single scan-derived observation.
 *
 * Last-write-wins (2026-07-18): a scan is just a statement about the cell at
 * time now, so it overwrites whatever is there — manual, trade, or an older
 * scan — with no source precedence. The only write we skip is an identical
 * scan re-observation (same state, already scan-sourced), so a steady stream
 * of frames does not churn `updatedAt`.
 */
export function applyScanObservation(
  variant: PokedexVariant,
  dexNumber: number,
  state: CellState,
  formId?: string | null,
): boolean {
  if (state === "unknown") return false;
  // pokedex-complete-dex: derived dexes are read-only — never persist a row.
  if (isDerivedVariant(variant)) return false;
  if (!Number.isFinite(dexNumber) || dexNumber < 1) return false;
  // Scanner integrity guard: a species not released in Pokémon GO cannot be seen
  // in the in-game Pokédex, so a scan "present" for one is impossible to obtain
  // legitimately — it means the scanner mis-mapped a cell to the wrong National
  // Dex id (the 2026-07-26 Galar false-writes). Reject it so it can never inflate
  // the dex above the released roster, and flag it loudly. isVariantAvailable
  // fails open when a roster is unknown, so a missing roster never blocks a real
  // write; "missing"/"seen" reads are left alone (they don't inflate catches).
  if (state === "present" && !isVariantAvailable(variant, dexNumber)) {
    recordRejectedUnreleasedScanWrite(variant, dexNumber);
    return false;
  }
  const store = safeReadStore();
  const key = keyFor(variant, dexNumber, formId);
  const prev = store[key];
  // Last-write-wins: the only skip is an identical scan re-observation (same
  // state AND already scan-sourced), which keeps updatedAt stable across a
  // frame burst. Any manual/trade row, or a scan row in a different state, is
  // overwritten below — the newest statement is the truth.
  if (prev && prev.state === state && prev.source === "scan") return false;
  const entry: DexEntryState = {
    variant, dexNumber, state, source: "scan", updatedAt: Date.now(),
    ...(formId ? { formId } : {}),
  };
  store[key] = entry;
  // b17d — a cascading-variant catch implies a MAIN catch (one-directional).
  // A valid Shadow/Purified catch likewise ticks the base Pokémon (All) dex.
  // Both results are captured (not discarded) so the scan session log can tell
  // undo whether THIS write is the reason the MAIN row is lit.
  let cascadedMain = false;
  if (state === "present") {
    const viaCascade = ensureMainCaughtFromCascade(store, variant, dexNumber);
    const viaRoster = ensureMainCaughtFromCuratedRoster(store, variant, dexNumber);
    cascadedMain = viaCascade || viaRoster;
  }
  // PR-E2c-cascade - a form catch (formId present) fans out to downstream
  // variants by CASCADE_GRAPH and ensures parent-species rows. Pure helper
  // mutates `store` in place and enqueues each cascaded row for server sync;
  // we call it before safeWriteStore so the whole batch persists atomically.
  if (state === "present" && formId && !isMegaGmaxVariant(variant)) {
    // pokedex-mega-xy: Mega/G-Max forms are independent dex cells (Charizard X
    // vs Y), NOT a "catch any form → species caught" case, so they must not
    // fan out to the base row or sibling forms. Mega catches carried no formId
    // before this, so exempting them changes nothing for existing behaviour.
    applyFormCascade(store, variant, dexNumber, formId, state);
  }
  // Mirror a two-form species' secondary cell when the BASE cell is caught
  // (formId absent). The detail/upload path does not scan Mega X/Y today, so
  // this is effectively a no-op here, but it keeps the "catch one → both" rule
  // true for every scan entry point rather than only the live grid batch.
  const cascadedForms =
    state === "present" && !formId
      ? [
          ...cascadeMegaGmaxForms(store, variant, dexNumber, "present", "scan", Date.now()),
          // …and the species' own base form row, so the sub-dex agrees with the
          // base cell and /compare stops reading the two as different copies.
          ...cascadeBaseSpeciesForm(store, variant, dexNumber, "scan", Date.now()),
        ]
      : [];
  safeWriteStore(store);
  emitChange();
  // b19-dex-server-sync: enqueue for debounced batch PUT.
  enqueuePending(key, entry);
  for (const c of cascadedForms) enqueuePending(c.key, c.entry);
  // Also enqueue any cascaded / linked MAIN entry.
  if (state === "present" && isMainLinkedVariant(variant)) {
    const mainKey = keyFor("pokemon", dexNumber);
    const mainEntry = store[mainKey];
    if (mainEntry) enqueuePending(mainKey, mainEntry);
  }
  // Log what CHANGED for the trainer, not what was written. A present read over
  // an already-present cell moved nothing into the collection (it only restamped
  // provenance), and a "missing" read of a cell that was never present is an
  // observation, not a removal — neither earns a row in the scan log.
  const wasPresent = prev?.state === "present";
  if (state === "present" && !wasPresent) {
    recordScanWrite({
      variant, dexNumber, formId: formId ?? null, action: "added", cascadedMain,
    });
  } else if (state !== "present" && wasPresent) {
    recordScanWrite({
      variant, dexNumber, formId: formId ?? null, action: "removed", cascadedMain: false,
    });
  }
  return true;
}

/**
 * SELF-CORRECTION: retract a catch this scanner previously wrote, when the
 * scanner has since built overwhelming evidence the cell is NOT caught.
 *
 * A false apply used to be permanent — the dex only ever ratcheted upward, so a
 * single bad frame silently corrupted a cell forever. This is the reverse
 * ratchet: sustained, high-confidence contrary reads flip a scan-written catch
 * back off (see dexScanSelfCorrection for the evidence threshold).
 *
 * HARD GUARANTEES:
 *   - a MANUAL entry is never touched — the user's explicit edit always wins;
 *   - only a `present` row written by a SCAN can be retracted;
 *   - nothing is retracted that this scanner did not write in the first place.
 * Returns true only when a row was actually flipped.
 */
export function retractScanPresent(
  variant: PokedexVariant,
  dexNumber: number,
  formId?: string | null,
  /**
   * 2026-09-24 owner direction: the blank-card path (a CENTRED number, GO's
   * uncaught layout, read over and over) may undo a catch whatever wrote it,
   * manual ticks included. Only dexScanSelfCorrection's blank-card evidence
   * passes this.
   */
  opts: { anySource?: boolean } = {},
): boolean {
  if (isDerivedVariant(variant)) return false;
  if (!Number.isFinite(dexNumber) || dexNumber < 1) return false;
  const store = safeReadStore();
  const key = keyFor(variant, dexNumber, formId);
  const prev = store[key];
  // Only ever undo OUR OWN scan-written catch, unless the blank-card path says
  // otherwise (opts.anySource).
  if (!prev || prev.state !== "present") return false;
  if (prev.source !== "scan" && !opts.anySource) return false;
  const entry: DexEntryState = {
    variant,
    dexNumber,
    state: "missing",
    source: "scan",
    updatedAt: Date.now(),
    ...(formId ? { formId } : {}),
  };
  store[key] = entry;
  // Clear the base-form mirror this catch wrote on the way in. Retracting the
  // base cell alone left `shiny:25:pikachu` claiming present forever — the same
  // stale second opinion the 2026-08-11 fix removed from the manual untick path,
  // which never reached this one. Without it the retraction is invisible to
  // anything that folds forms onto the base (Compare), and an undo of a variant
  // catch could not tell that the catch was gone.
  const clearedMirror = formId
    ? []
    : clearBaseSpeciesFormMirror(store, variant, dexNumber, "missing", "scan", Date.now());
  safeWriteStore(store);
  emitChange();
  enqueuePending(key, entry);
  for (const c of clearedMirror) enqueuePending(c.key, c.entry);
  recordScanWrite({
    variant, dexNumber, formId: formId ?? null, action: "removed", cascadedMain: false,
  });
  // eslint-disable-next-line no-console
  console.log("[dex-scan] self-correction: retracted scan catch", { variant, dexNumber, priorSource: prev.source });
  return true;
}

/**
 * Undo one scan-written catch on the trainer's instruction (the Undo button on
 * a scan-history row), including the MAIN row the write cascaded into.
 *
 * `retractScanPresent` alone is not enough for a variant catch. A scan that read
 * a shiny Pikachu writes TWO rows: `shiny:25` and, through the cascade, a MAIN
 * `pokemon:25`. Retracting only the shiny row leaves the trainer's Main dex
 * still claiming a Pikachu that the scan — not the trainer — put there, which is
 * exactly the wrong-scan case the button exists to fix.
 *
 * The MAIN row is only dropped when all three hold, so undo can never take away
 * a catch it did not create:
 *   - no OTHER main-linked variant still holds a present row for the species
 *     (a real Lucky Pikachu keeps MAIN lit on its own);
 *   - the MAIN row is still `source: "scan"` — a manual or trade row is the
 *     trainer's own statement and is never touched;
 *   - the retraction of the variant row itself actually landed.
 *
 * The recorder is suspended for the duration: an undo is the trainer editing the
 * log, not a scan write, and must not append a "removed" row to a session that
 * happens to be open.
 */
export function undoScanWrite(
  variant: PokedexVariant,
  dexNumber: number,
  formId?: string | null,
): boolean {
  const suspended = scanWriteRecorder;
  scanWriteRecorder = null;
  try {
    if (!retractScanPresent(variant, dexNumber, formId)) return false;
    if (!isMainLinkedVariant(variant)) return true;
    const store = safeReadStore();
    // Form-qualified rows live under their own keys, so this walks entries
    // rather than probing `keyFor(v, dexNumber)` — a present `shiny:25:alola`
    // must keep MAIN lit just as a present `shiny:25` would.
    for (const entry of Object.values(store)) {
      if (entry.dexNumber !== dexNumber) continue;
      if (entry.state !== "present") continue;
      if (!isMainLinkedVariant(entry.variant)) continue;
      return true;
    }
    const mainKey = keyFor("pokemon", dexNumber);
    const main = store[mainKey];
    if (!main || main.state !== "present" || main.source !== "scan") return true;
    const cleared: DexEntryState = {
      variant: "pokemon",
      dexNumber,
      state: "missing",
      source: "scan",
      updatedAt: Date.now(),
    };
    store[mainKey] = cleared;
    safeWriteStore(store);
    emitChange();
    enqueuePending(mainKey, cleared);
    return true;
  } finally {
    scanWriteRecorder = suspended;
  }
}

/**
 * One-shot data reconciliation: retract scan-written catches for species that
 * are NOT released in Pokémon GO for their variant.
 *
 * The scanner integrity guard (applyScanObservation) blocks such writes going
 * forward, but accounts scanned before the guard still carry the bad rows — the
 * 2026-07-26 Galar false-writes that pushed a Main count above the released
 * roster. This walks the local store (the device's source of truth) and retracts
 * every scan-sourced `present` entry the released roster does not allow, so the
 * fix syncs up to the server backup too (retractScanPresent enqueues the change).
 *
 * SAFE BY CONSTRUCTION:
 *   - only `source === "scan"` present rows are touched — a manual or trade catch
 *     is never removed;
 *   - isVariantAvailable fails open on an unknown roster, so a missing roster
 *     never deletes a legitimate catch;
 *   - idempotent — after the first pass there is nothing left to retract, so it
 *     is cheap to run on every launch and keeps the invariant enforced if a
 *     future roster correction un-releases a species.
 *
 * Returns what it removed so the caller can log a one-line summary.
 */
export function reconcileUnreleasedScanCatches(): {
  removed: number;
  ids: Array<{ variant: PokedexVariant; dexNumber: number }>;
} {
  if (typeof localStorage === "undefined") return { removed: 0, ids: [] };
  const store = safeReadStore();
  // Collect first, then retract — retractScanPresent rewrites the store on each
  // call, so we must not mutate while iterating the snapshot.
  const targets: Array<{ variant: PokedexVariant; dexNumber: number; formId: string | null }> = [];
  for (const key in store) {
    const e = store[key];
    if (!e || e.state !== "present" || e.source !== "scan") continue;
    if (isDerivedVariant(e.variant)) continue;
    if (isVariantAvailable(e.variant, e.dexNumber)) continue; // released → keep
    targets.push({ variant: e.variant, dexNumber: e.dexNumber, formId: e.formId ?? null });
  }
  let removed = 0;
  for (const t of targets) {
    if (retractScanPresent(t.variant, t.dexNumber, t.formId)) removed += 1;
  }
  if (removed > 0) {
    // eslint-disable-next-line no-console
    console.warn(
      `[dex-scan] reconcile: retracted ${removed} scan catch(es) for species not released in ` +
        `Pokémon GO (scanner false-writes): ` +
        targets.map((t) => `${t.variant}#${t.dexNumber}`).join(", "),
    );
  }
  return { removed, ids: targets.map((t) => ({ variant: t.variant, dexNumber: t.dexNumber })) };
}

/**
 * One-shot data reconciliation: retract catches on the trade-only dexes for
 * species Pokémon GO does not allow to be traded.
 *
 * Sibling of reconcileUnreleasedScanCatches, for the hole that one deliberately
 * leaves: it only ever touches SCAN rows, because a release roster can be wrong
 * and a manual catch must not be deleted over our bad data. This one may touch a
 * manual (or trade) row precisely because its input is different in kind — the
 * GO Game Master's own `isTradable` flag, a hard rule of the game rather than a
 * roster we curate. No trade, no Lucky, so the row cannot describe anything real.
 *
 * The 2026-08-07 case: a hand-tapped Lucky Keldeo #647 counted on the National
 * ring (921) but not on GO-Released (920), because Keldeo is released and
 * untradable. The write guard in setManual blocks new ones; this clears the rows
 * already on the device and syncs the correction up.
 *
 * SAFE BY CONSTRUCTION:
 *   - only `lucky` / `shinyLucky` rows are eligible (isImpossibleForVariant);
 *   - only ids the Game Master positively marks untradable — anything outside its
 *     coverage fails open and is left alone;
 *   - release state is NOT consulted, so an incomplete released roster can never
 *     delete a legitimate catch;
 *   - a tombstone is recorded per row so a stale server `present` cannot re-import
 *     the row on the next pull;
 *   - idempotent — after one pass there is nothing left, so it is cheap on every
 *     launch and keeps the invariant true if the roster is ever regenerated.
 */
export function reconcileUntradableLuckyCatches(): {
  removed: number;
  ids: Array<{ variant: PokedexVariant; dexNumber: number }>;
} {
  if (typeof localStorage === "undefined") return { removed: 0, ids: [] };
  const store = safeReadStore();
  const targets: DexEntryState[] = [];
  for (const key in store) {
    const e = store[key];
    if (!e || e.state !== "present") continue;
    if (isDerivedVariant(e.variant)) continue;
    if (!isImpossibleForVariant(e.variant, e.dexNumber)) continue;
    targets.push(e);
  }
  if (targets.length === 0) return { removed: 0, ids: [] };
  const now = Date.now();
  const written: Array<{ key: string; entry: DexEntryState }> = [];
  for (const t of targets) {
    const key = keyFor(t.variant, t.dexNumber, t.formId);
    // "missing", not a deletion: a removed row would simply be re-imported from
    // the server backup, while a `missing` statement syncs the correction up.
    // `source` is preserved as provenance — this rewrites the state, and lying
    // about who wrote the row would only confuse the badge in the grid.
    const entry: DexEntryState = {
      variant: t.variant,
      dexNumber: t.dexNumber,
      state: "missing",
      source: t.source,
      updatedAt: now,
      ...(t.formId ? { formId: t.formId } : {}),
    };
    store[key] = entry;
    recordEntryTombstone(t.variant, t.dexNumber, now);
    written.push({ key, entry });
  }
  if (!safeWriteStore(store)) return { removed: 0, ids: [] };
  emitChange();
  for (const w of written) enqueuePending(w.key, w.entry);
  // eslint-disable-next-line no-console
  console.warn(
    `[dex] reconcile: retracted ${written.length} catch(es) on a trade-only dex for ` +
      `species Pokémon GO cannot trade: ` +
      targets.map((t) => `${t.variant}#${t.dexNumber}`).join(", "),
  );
  return {
    removed: written.length,
    ids: targets.map((t) => ({ variant: t.variant, dexNumber: t.dexNumber })),
  };
}

/**
 * Floor for "the form catalogue actually loaded". The committed catalogue carried
 * 764 form ids on 2026-09-01, and the failure this guards against is a module that
 * loaded empty or truncated, which lands near zero rather than just under the floor.
 * The number is a smoke test, not a target: it is deliberately far below the real
 * size so that ordinary growth and shrinkage never approach it, and if a genuine
 * catalogue ever falls near it the answer is to re-derive the floor, never to assume
 * Pokémon GO retired most of its forms at once.
 */
const MIN_PLAUSIBLE_FORM_CATALOGUE = 500;

/**
 * Most rows one catalogue edit may retract in a single pass. The 2026-08-19 removal
 * of 61 not-in-GO forms was the largest deliberate change to date and no single
 * trainer held anywhere near that many of them. A batch above this is a catalogue
 * problem wearing a reconciliation costume.
 */
const MAX_ORPHANED_FORM_RETRACTIONS = 40;

/**
 * Orphaned form ids whose catch FOLDS into a form the catalogue still has,
 * instead of being retracted (2026-10-07). Minior's seven Meteor shells left
 * the Forms grid (owner direction: the colours are the collection); a trainer
 * who had ticked "Red Meteor" owns a red Minior, so the catch moves to "Red".
 * The old row is still marked missing so the retraction syncs and /compare
 * stops emitting the orphan as a lead.
 */
const ORPHANED_FORM_FOLDS: Readonly<Record<string, string>> = Object.fromEntries(
  ["red", "orange", "yellow", "green", "blue", "indigo", "violet"].map((c) => [
    `minior-${c}-meteor`,
    `minior-${c}`,
  ]),
);

/**
 * Retract catches on FORMS that no longer exist in the app's form catalogue.
 *
 * Third sibling of reconcileUnreleasedScanCatches / reconcileUntradableLuckyCatches,
 * for the case where the CATALOGUE changes under rows that were legitimate when
 * they were written. 2026-08-19 removed 61 forms that PokéAPI and the GO Game
 * Master carry but Pokémon GO never grants — Darmanitan Zen, Aegislash Blade,
 * the seventeen Arceus plates, the seventeen Silvally memories and the rest (see
 * scripts/formReleaseGate.ts). Anyone who had ticked one kept the row.
 *
 * WHY THE ROW IS NOT HARMLESS, which is what it looked like at first: the
 * species page builds its form grid FROM the catalogue, so a removed form has no
 * cell and the row does stop rendering there. But /compare does not read the
 * catalogue — it emits one lead per (variant, species, formId) straight from
 * dex_records, folding only the BASE form to null. An orphaned form row is not
 * the base form, so it survives the fold and becomes its own trade lead: a
 * second, unlabelled "Darmanitan" beside the real one, for a copy that cannot
 * exist. `formDisplayName` returns null for it, so nothing on the tile even says
 * which one it is. This is the Gimmighoul Roaming bug (2026-08-07) recurring
 * through a different door.
 *
 * MARKED MISSING, NOT DELETED — same reason as its siblings: a deleted row is
 * re-imported from the server backup on the next pull, while a `missing` state
 * syncs the retraction up and clears the lead on BOTH sides of the comparison.
 *
 * NO TOMBSTONE, and this is the one thing that must not be copied from
 * reconcileUntradableLuckyCatches. Tombstones are keyed (variant, dexNumber)
 * with NO form, and mainPresentMissingSets reads them as "the trainer says they
 * do not have this species". Writing one here would retract the SPECIES over a
 * form correction — and the species catch is real: a form catch cascades into a
 * base-species row (applyFormCascade), the trainer does own a Darmanitan, and
 * that base row is deliberately left untouched. Only rows that carry a formId
 * are eligible.
 *
 * SAFE BY CONSTRUCTION:
 *   - base-species rows (no formId) are never touched, so no catch is lost;
 *   - Mega X/Y and G-Max Single/Rapid cells live outside POKEMON_FORM_GROUPS and
 *     are allow-listed explicitly, so the two dexes keyed on those ids survive;
 *   - a formId the catalogue still knows is left alone, so this can only ever
 *     act on a form the app has genuinely stopped modelling;
 *   - idempotent — one pass leaves nothing to do, so it is cheap on every launch
 *     and keeps the invariant true the next time the roster is regenerated;
 *   - FAILS OPEN on an unusable catalogue and refuses batches above
 *     MAX_ORPHANED_FORM_RETRACTIONS, so a bad regen cannot turn one launch into a
 *     mass retraction that syncs to the server (added 2026-09-01; absence from a
 *     generated file is not by itself evidence a form was retired).
 */
export function reconcileOrphanedFormCatches(): {
  removed: number;
  ids: Array<{ variant: PokedexVariant; dexNumber: number; formId: string }>;
} {
  if (typeof localStorage === "undefined") return { removed: 0, ids: [] };
  // FAIL OPEN ON AN UNUSABLE CATALOGUE, the guard its two siblings always had and
  // this one did not. A Map lookup cannot tell "this form was deliberately removed"
  // from "the catalogue did not load", and both answer `has() === false`, so an
  // empty or truncated generated file would read as "every form the trainer owns
  // has been retired" and retract the lot. Treat an implausibly small catalogue as
  // no information rather than as a mass deletion order.
  if (FORM_GROUPS_BY_FORM_ID.size < MIN_PLAUSIBLE_FORM_CATALOGUE) {
    // eslint-disable-next-line no-console
    console.warn(
      `[dex] reconcile: form catalogue looks unusable (${FORM_GROUPS_BY_FORM_ID.size} ` +
        `form ids, expected at least ${MIN_PLAUSIBLE_FORM_CATALOGUE}); retracting nothing.`,
    );
    return { removed: 0, ids: [] };
  }
  const store = safeReadStore();
  const targets: DexEntryState[] = [];
  for (const key in store) {
    const e = store[key];
    if (!e || e.state !== "present" || !e.formId) continue;
    if (isDerivedVariant(e.variant)) continue;
    if (FORM_GROUPS_BY_FORM_ID.has(e.formId)) continue; // still a real form
    if (isMegaGmaxExtraFormId(e.formId)) continue; // its own dex, own id space
    targets.push(e);
  }
  if (targets.length === 0) return { removed: 0, ids: [] };
  // BLAST-RADIUS CAP. A correct run clears the handful of rows left behind by one
  // catalogue edit. A run that wants to retract more than this is not reconciling,
  // it is reacting to a catalogue that changed shape underneath it (a renamed id
  // space, a widened not-in-GO filter, a bad regen), and the trainer's rows are the
  // wrong thing to sacrifice to it. Refuse the whole batch: doing nothing is
  // recoverable on the next launch, and a mass retraction syncs up and is not.
  if (targets.length > MAX_ORPHANED_FORM_RETRACTIONS) {
    // eslint-disable-next-line no-console
    console.warn(
      `[dex] reconcile: ${targets.length} orphaned form rows exceeds the ` +
        `${MAX_ORPHANED_FORM_RETRACTIONS}-row cap, retracting NOTHING. This means the form ` +
        `catalogue changed shape, not that the trainer lost these forms. Sample: ` +
        targets.slice(0, 8).map((t) => `${t.variant}#${t.dexNumber}:${t.formId}`).join(", "),
    );
    return { removed: 0, ids: [] };
  }
  const now = Date.now();
  const written: Array<{ key: string; entry: DexEntryState }> = [];
  let folded = 0;
  for (const t of targets) {
    const key = keyFor(t.variant, t.dexNumber, t.formId);
    const entry: DexEntryState = {
      variant: t.variant,
      dexNumber: t.dexNumber,
      state: "missing",
      // Provenance is preserved: this rewrites the state, and claiming the
      // trainer had never made the mark would only confuse the grid badge.
      source: t.source,
      updatedAt: now,
      formId: t.formId,
    };
    store[key] = entry;
    written.push({ key, entry });
    // The catch itself moves to the form it folds into, when that form is
    // real and not already ticked (ORPHANED_FORM_FOLDS).
    const foldTo = t.formId ? ORPHANED_FORM_FOLDS[t.formId] : undefined;
    if (foldTo && FORM_GROUPS_BY_FORM_ID.has(foldTo)) {
      const foldKey = keyFor(t.variant, t.dexNumber, foldTo);
      if (store[foldKey]?.state !== "present") {
        const foldEntry: DexEntryState = {
          variant: t.variant,
          dexNumber: t.dexNumber,
          state: "present",
          source: t.source,
          updatedAt: now,
          formId: foldTo,
        };
        store[foldKey] = foldEntry;
        written.push({ key: foldKey, entry: foldEntry });
        folded += 1;
      }
    }
  }
  if (!safeWriteStore(store)) return { removed: 0, ids: [] };
  emitChange();
  for (const w of written) enqueuePending(w.key, w.entry);
  // eslint-disable-next-line no-console
  console.warn(
    `[dex] reconcile: retracted ${targets.length} catch(es) on form(s) Pokémon GO does ` +
      `not have (${folded} folded into their current form): ` +
      targets.map((t) => `${t.variant}#${t.dexNumber}:${t.formId}`).join(", "),
  );
  return {
    removed: targets.length,
    ids: targets.map((t) => ({
      variant: t.variant,
      dexNumber: t.dexNumber,
      formId: t.formId as string,
    })),
  };
}

/** Mega X/Y and G-Max Single/Rapid cells are keyed by ids outside the catalogue. */
function isMegaGmaxExtraFormId(formId: string): boolean {
  return (
    MEGA_EXTRA_FORMS.some((f) => f.formId === formId) ||
    GMAX_EXTRA_FORMS.some((f) => f.formId === formId)
  );
}

/**
 * Read the most recent scan-derived state for an entry. Returns the
 * `manualBaseline` captured when the manual override was created, or
 * the current state if the entry is currently scan-sourced, or null.
 *
 * Used by the Pokédex grid + manual-toggle UI to decide whether a
 * tap returns the entry to its original scan state (which clears the
 * "M" badge) vs creates a fresh manual override.
 */
export function getScanBaseline(
  variant: PokedexVariant,
  dexNumber: number,
  formId?: string | null,
): CellState | null {
  const entry = getDexEntryState(variant, dexNumber, formId);
  if (!entry) return null;
  if (entry.source === "scan") return entry.state;
  return entry.manualBaseline ?? null;
}

/**
 * Apply a batch of scan observations. Returns the number actually persisted.
 *
 * `skippedManual` is a LEGACY counter that is always 0: since the 2026-07-18
 * last-write-wins change, manual entries are NOT skipped (a fresh scan is the
 * newest statement about a cell and supersedes them - see dexEntryResolver).
 * The field survives only because the live-scan diagnostics payload shape
 * threads it end-to-end; do not branch on it. (2026-08-30 audit: the old
 * one-line doc here still claimed manual entries were skipped.)
 */
export function applyScanObservations(
  variant: PokedexVariant,
  observations: Array<{ dexNumber: number; state: CellState }>,
): { applied: number; skippedManual: number; presentIds: number[] } {
  if (typeof localStorage === "undefined") {
    return { applied: 0, skippedManual: 0, presentIds: [] };
  }
  // pokedex-complete-dex: derived dexes have no scan path — refuse the batch.
  if (isDerivedVariant(variant)) {
    return { applied: 0, skippedManual: 0, presentIds: [] };
  }
  // [dex-scan] probe: the JS dex pipeline reached the write path. Seeing this
  // in a device log proves observations flowed all the way to dexStateStore
  // (the native QA LiveScanPipeline never calls this).
  //
  // Build 5 / v1.4.6 — `count` and `ids` MUST describe the same batch. The
  // Build 4 log truncated `ids` to the first 5 while `count` stayed at the full
  // length, so the device log read `count:7 ids:[5,6,7,8,10]` and looked like
  // the pipeline was dropping IDs between OCR and apply. It was only the log
  // that lied — every observation is written below. We now log the FULL id list
  // and derive `count` from it so the two can never diverge again.
  const ids = observations.map((o) => o.dexNumber);
  // eslint-disable-next-line no-console
  console.log("[dex-scan] apply observations", {
    variant,
    count: ids.length,
    ids,
  });
  const store = safeReadStore();
  let applied = 0;
  let skippedManual = 0;
  let touched = false;
  // Dex numbers this batch actually WROTE as "present" (a genuine new-to-dex
  // caught write). Drives the committed-only "Added" toast: a seen-only
  // ("missing") write, an unchanged re-observation, and a manual-override skip
  // all stay out of this set, so the toast can never announce more than landed.
  const presentIds: number[] = [];
  /**
   * What this batch CHANGED, collected as we go and reported to the scan log
   * only once the durable write has actually landed.
   *
   * This batch path writes into `store` directly rather than calling
   * applyScanObservation, so hooking the recorder into that function alone
   * covered the detail-page scan and missed the live grid — which is the path
   * essentially every scan takes. A 67-entry scan logged nothing.
   */
  const changes: ScanWriteRecord[] = [];
  for (const obs of observations) {
    if (obs.state === "unknown") continue;
    if (!Number.isFinite(obs.dexNumber) || obs.dexNumber < 1) continue;
    // Scanner integrity guard (see applyScanObservation): drop a "present" write
    // for a species not released in GO and flag the scanner mis-map, so a
    // mis-read cell can never be committed as a catch.
    if (obs.state === "present" && !isVariantAvailable(variant, obs.dexNumber)) {
      recordRejectedUnreleasedScanWrite(variant, obs.dexNumber);
      continue;
    }
    const key = keyFor(variant, obs.dexNumber);
    const prev = store[key];
    if (prev && prev.state === obs.state && prev.source === "scan") continue;
    const wasPresent = prev?.state === "present";
    store[key] = {
      variant, dexNumber: obs.dexNumber, state: obs.state,
      source: "scan", updatedAt: Date.now(),
    };
    if (obs.state === "present") presentIds.push(obs.dexNumber);
    // Same rule as the single-observation path: a re-observation of a cell that
    // was already present moved nothing into the collection, and a "missing"
    // read of a cell that was never present is an observation, not a removal.
    if (obs.state === "present" && !wasPresent) {
      changes.push({
        variant, dexNumber: obs.dexNumber, formId: null,
        action: "added", cascadedMain: false,
      });
    } else if (obs.state !== "present" && wasPresent) {
      changes.push({
        variant, dexNumber: obs.dexNumber, formId: null,
        action: "removed", cascadedMain: false,
      });
    }
    // b17d — a cascading-variant catch implies a MAIN catch (one-directional):
    // any species caught as shiny/lucky/xxl/xxs/perfect is also caught in the
    // MAIN dex. A "missing"/"unknown" variant read never triggers this, so a
    // shiny silhouette can never clear an existing MAIN catch.
    if (obs.state === "present") {
      ensureMainCaughtFromCascade(store, variant, obs.dexNumber);
      // A valid Shadow/Purified catch also ticks the base Pokémon (All) dex.
      // The roster check inside the helper means a blocked/out-of-roster id can
      // never tick MAIN through this path.
      ensureMainCaughtFromCuratedRoster(store, variant, obs.dexNumber);
    }
    touched = true;
    applied += 1;
    // b19-dex-server-sync: enqueue written entry for debounced PUT.
    enqueuePending(key, store[key]);
    // Mirror a two-form species' secondary cell (Mega X→Y etc.) so a scan of
    // the base cell fills BOTH. No-op for every single-cell species.
    if (obs.state === "present") {
      for (const c of cascadeMegaGmaxForms(store, variant, obs.dexNumber, "present", "scan", Date.now())) {
        applied += 1;
        enqueuePending(c.key, c.entry);
      }
      // Synced but deliberately NOT counted: the base-form row mirrors a cell
      // the trainer already sees as caught, so counting it would report twice
      // the number of species they actually added.
      for (const c of cascadeBaseSpeciesForm(store, variant, obs.dexNumber, "scan", Date.now())) {
        enqueuePending(c.key, c.entry);
      }
    }
  }
  if (touched) {
    // PiP-trust fix: if the durable write fails (iOS WKWebView blocked
    // storage, quota exceeded), nothing actually persisted. We must NOT report
    // a committed result, or the PiP/toast will claim success while the dex is
    // unchanged. Return a zero-commit result (applied 0, no presentIds) so the
    // live-scan session announces no new entries. skippedManual is preserved:
    // those rows were genuinely skipped by the manual-override gate regardless
    // of whether the remaining writes persisted.
    if (!safeWriteStoreCoalesced(store)) {
      // eslint-disable-next-line no-console
      console.warn(
        "[dex-scan] safeWriteStore failed, reporting zero committed entries",
        { variant, attempted: applied },
      );
      return { applied: 0, skippedManual, presentIds: [] };
    }
    emitChange();
    // Only now — a scan log that recorded writes the store rejected would show
    // the trainer entries their dex does not have, with an Undo for each.
    for (const change of changes) recordScanWrite(change);
    // Enqueue any cascade-/linkage-written MAIN entries.
    for (const obs of observations) {
      if (obs.state === "present" && isMainLinkedVariant(variant)) {
        const mainKey = keyFor("pokemon", obs.dexNumber);
        const mainEntry = store[mainKey];
        if (mainEntry) enqueuePending(mainKey, mainEntry);
      }
    }
  }
  return { applied, skippedManual, presentIds };
}

/**
 * Set a state manually. This is the user's "I have this one" / "I don't
 * have this one" toggle from the Dex page. It writes unconditionally (no
 * `force` flag needed): under last-write-wins this edit is the newest
 * statement about the cell and simply overwrites whatever was there. Toggling
 * back to the captured scan baseline instead reverts to the scan row (drops the
 * manual badge) — see the baseline handling below.
 */
export function setManual(
  variant: PokedexVariant,
  dexNumber: number,
  state: CellState,
  formId?: string | null,
): boolean {
  if (!Number.isFinite(dexNumber) || dexNumber < 1) return false;
  // A tick on the Regional / Costume dex IS a tick on the species page's own
  // form cell, so it writes that row rather than a parallel one. Normalised
  // before anything reads `variant`, so the key, the stored row, the cascade,
  // the tombstone and the sync payload all agree. See storageVariantFor.
  variant = storageVariantFor(variant);
  // pokedex-complete-dex: derived dexes are computed, never manually set.
  if (isDerivedVariant(variant)) return false;
  if (state === "unknown") return clearManual(variant, dexNumber, formId);
  // Trade-gate integrity (2026-08-08): a species Pokémon GO refuses to trade can
  // never be Lucky / Shiny-Lucky, so a hand-tapped "present" on one is not a
  // catch we can honour. Left unguarded it inflated the National ring above a
  // denominator that correctly has no slot for it — a Lucky Keldeo #647 read
  // 921/1,002 while GO-Released read 920/933.
  //
  // Scoped to that STRUCTURAL impossibility on purpose (isImpossibleForVariant,
  // not !isVariantAvailable): a species merely missing from our released roster
  // stays hand-markable, because that roster is chronically incomplete in both
  // directions and the manual tap is the trainer's only way to correct it.
  if (state === "present" && isImpossibleForVariant(variant, dexNumber)) {
    // eslint-disable-next-line no-console
    console.warn(
      `[dex] rejected manual ${variant} catch for #${dexNumber}, Pokémon GO does ` +
        `not allow this species to be traded, so it can never be ${variant}.`,
    );
    return false;
  }
  const store = safeReadStore();
  const key = keyFor(variant, dexNumber, formId);
  const prev = store[key];

  // Push 6 / v1.4.2 b2: derive the scan baseline so a user toggling back
  // to the original scan-derived value clears the manual override (and
  // therefore drops the "M" badge in the grid).
  //   - If we're replacing a scan row, the scan row IS the baseline.
  //   - If we're replacing an existing manual row, preserve whatever
  //     baseline was captured the first time the override was made.
  //   - If no prior row exists, the baseline is null (a manual-only mark).
  const baseline: CellState | undefined =
    prev?.source === "scan"
      ? prev.state
      : prev?.manualBaseline;

  if (baseline != null && state === baseline) {
    // Reverting to the original scan state: drop the manual override
    // and restore the scan row so the UI shows the baseline without
    // an "M" badge.
    const revertEntry: DexEntryState = {
      variant, dexNumber, state: baseline, source: "scan",
      updatedAt: Date.now(),
      ...(formId ? { formId } : {}),
    };
    store[key] = revertEntry;
    const revertPersisted = safeWriteStore(store);
    emitChange();
    // b19-dex-server-sync: enqueue reverted entry.
    enqueuePending(key, revertEntry);
    return revertPersisted;
  }

  // PR V: a user-initiated present -> missing transition is an explicit
  // removal. Record a per-entry reconciliation tombstone so a later
  // pullDexStateFromServer cannot silently re-import a stale server row for
  // this (variant, dexId) that predates the removal. A newer server row (the
  // user re-added the species on another device) still wins; the tombstone is
  // cleared then in pullDexStateFromServer.
  if (state === "missing" && prev?.state === "present") {
    recordEntryTombstone(variant, dexNumber);
  }

  const manualEntry: DexEntryState = {
    variant, dexNumber, state, source: "manual", updatedAt: Date.now(),
    ...(baseline != null ? { manualBaseline: baseline } : {}),
    ...(formId ? { formId } : {}),
  };
  store[key] = manualEntry;
  // PR-E2c-cascade - manual form catch fans out via CASCADE_GRAPH and lights
  // up parent-species rows ("if any form caught, species caught"). Same
  // semantics as the scan path: only "present" cascades, manual "missing" on
  // target rows is respected, existing "present" never downgraded.
  if (state === "present" && formId && !isMegaGmaxVariant(variant)) {
    // pokedex-mega-xy: Mega/G-Max forms are independent dex cells (Charizard X
    // vs Y), NOT a "catch any form → species caught" case, so they must not
    // fan out to the base row or sibling forms. Mega catches carried no formId
    // before this, so exempting them changes nothing for existing behaviour.
    applyFormCascade(store, variant, dexNumber, formId, state);
  }
  // A valid Shadow/Purified manual catch also ticks the base Pokémon (All) dex
  // for the same id (one-directional, roster-gated). Runs only after the manual
  // write itself is committed to `store`, so a blocked id never reaches MAIN.
  let mainLinked = false;
  if (state === "present") {
    mainLinked = ensureMainCaughtFromCuratedRoster(store, variant, dexNumber);
    // …and so does a manual Shiny/Lucky/XXL/XXS/100% catch. This ran on the
    // scan and trade paths (applyScanObservation, markTradeCaught) but not
    // here, so a HAND-marked variant catch left no physical MAIN row. The grid
    // draws the MAIN cell from what is physically written and showed a
    // silhouette, while the count applied its read-time cascade and called the
    // species caught — the same grid-vs-total disagreement an untick caused,
    // reached by a different route. Writing the row keeps every surface
    // reading one answer.
    const cascadeLinked = ensureMainCaughtFromCascade(store, variant, dexNumber);
    mainLinked = mainLinked || cascadeLinked;
  }
  // Ticking the BASE cell by hand also fills the species' own base form row, so
  // the sub-dex agrees with the cell above it. Only fires when this write is the
  // base one (no formId) — a form write already cascades the other way.
  //
  // …and CLEARING it must clear the mirror too (2026-08-11). The mirror used to
  // be strictly one-directional — `cascadeBaseSpeciesForm` hard-codes
  // `state: "present"` and only ran on a present write — so ticking a species
  // wrote BOTH rows while unticking wrote only the base one, leaving the base
  // form row stuck on a stale `present`. The two rows describe the SAME Pokémon
  // (that is the whole premise of the server's base-form fold), so a mirror that
  // can only ever be SET is a mirror that drifts.
  //
  // What the leak surfaced as: the Compare API loads `state = 'present'` rows and
  // only THEN folds base-form onto base, so a corrected `missing` base row is
  // filtered out before the fold can out-vote the stale mirror — and the mirror
  // is rewritten into a base row. "You have, they don't" listed a Shiny Tauros
  // that reads as missing in the dex. 182 rows across 3 accounts.
  const baseFormRows = formId
    ? []
    : state === "present"
      ? cascadeBaseSpeciesForm(store, variant, dexNumber, "manual", Date.now())
      : clearBaseSpeciesFormMirror(store, variant, dexNumber, state, "manual", Date.now());
  // PiP-trust, manual edition: a blocked/quota localStorage write means nothing
  // persisted durably (the in-memory cache keeps the edit for THIS session, so
  // the grid still reflects it, and a later successful write re-persists it).
  // Callers that report a committed result to the user (the gap strips' "Added
  // N to your Nexus Dex" commit) must hear about it rather than toasting
  // success over a write that will evaporate on the next launch — the exact
  // "quick add looked fine, dex never updated" failure applyScanObservations
  // already guards against.
  const persisted = safeWriteStore(store);
  emitChange();
  // b19-dex-server-sync: enqueue manual entry.
  enqueuePending(key, manualEntry);
  for (const c of baseFormRows) enqueuePending(c.key, c.entry);
  if (mainLinked) {
    const mainKey = keyFor("pokemon", dexNumber);
    const mainEntry = store[mainKey];
    if (mainEntry) enqueuePending(mainKey, mainEntry);
  }
  return persisted;
}

/**
 * b19-feat-linked-dex: declarative map of which custom dexes propagate to
 * which base dexes. Downstream-only by design: base-dex removals do not
 * prompt about custom dexes. This is intentionally NOT merged with
 * CASCADING_VARIANTS (which is the read-side cascade to MAIN); they answer
 * different questions. CASCADING_VARIANTS defines what counts toward the MAIN
 * summary at read time; CUSTOM_DEX_LINKAGE defines what extra writes a custom
 * dex add (or confirmed removal) performs.
 */
export const CUSTOM_DEX_LINKAGE: Partial<Record<PokedexVariant, readonly PokedexVariant[]>> = {
  shundo: ["shiny", "perfect", "pokemon"],
  shinyLucky: ["shiny", "lucky", "pokemon"],
  // dmax-dynamax-dex: catching a Dynamax species is a real catch of the base
  // species, so marking it present also ticks MAIN.
  dynamax: ["pokemon"],
};

/**
 * Like setManual, but for custom-dex entries: when adding "present", also
 * tick the linked variants (Shiny Perfect -> Shiny + Perfect + Main).
 * When the linkage pref is OFF, behaves exactly like setManual.
 *
 * Idempotent: if a linked variant is already present, the extra setManual is
 * effectively a no-op (it just reasserts present).
 *
 * Only the "present" state propagates. A "missing"/"unknown" mark on a custom
 * dex does not auto-clear the linked base dexes here; the LinkedRemovalSheet
 * uses removeWithLinks for the confirmed-removal path instead.
 */
export function setManualWithLinks(
  variant: PokedexVariant,
  dexNumber: number,
  state: CellState,
  opts?: { propagate?: boolean },
): boolean {
  const ok = setManual(variant, dexNumber, state);
  if (state !== "present") return ok;
  const propagate = opts?.propagate ?? isCustomDexLinkageEnabled();
  if (!propagate) return ok;
  const linked = CUSTOM_DEX_LINKAGE[variant];
  if (!linked) return ok;
  for (const v of linked) {
    setManual(v, dexNumber, "present");
    // eslint-disable-next-line no-console
    console.log("[dex-linkage] propagated", { source: variant, target: v, dexNumber });
  }
  return ok;
}

/**
 * Remove from a primary custom variant plus the explicitly listed linked
 * variants. Called by LinkedRemovalSheet after the user confirms which dexes
 * to clear. Each variant is set to "missing" via setManual.
 */
export function removeWithLinks(
  variant: PokedexVariant,
  dexNumber: number,
  alsoRemoveFrom: readonly PokedexVariant[],
): void {
  setManual(variant, dexNumber, "missing");
  for (const v of alsoRemoveFrom) {
    setManual(v, dexNumber, "missing");
  }
}

/**
 * Toggle present <-> missing for a manual override. Idempotent for the
 * UI: a long-press to set to "present" can be undone by tapping again.
 *
 * Push 6 / v1.4.2 b2: when there is no prior state at all, the first
 * tap creates a fresh manual "present" mark (the user said "I have
 * this"). Subsequent taps flip; if the flip equals the original scan
 * baseline (captured by setManual), the manual override is cleared.
 */
export function toggleManual(
  variant: PokedexVariant,
  dexNumber: number,
  formId?: string | null,
): DexEntryState | null {
  const cur = getDexEntryState(variant, dexNumber, formId);
  // No prior state — first manual tap should mark "present" (the
  // intuitive "I have this one") rather than "missing", which would
  // be invisible in the grid. The one exception (2026-08-31): the
  // species' MIRROR form cell renders lit off the species-level row when
  // it has no row of its own (FormGrid's read-side fallback), so the
  // user is looking at a ticked cell — their tap means "untick", and
  // writing "present" here would visibly do nothing.
  const litByMirrorFallback =
    cur == null &&
    formId != null &&
    mirrorFormIdForSpecies(dexNumber) === formId &&
    getDexEntryState(variant, dexNumber, null)?.state === "present";
  const next: CellState = cur == null
    ? (litByMirrorFallback ? "missing" : "present")
    : (cur.state === "present" ? "missing" : "present");
  setManual(variant, dexNumber, next, formId);
  return getDexEntryState(variant, dexNumber, formId);
}

/**
 * Mark a species present from a completed trade (b19-trade-plan-to-done).
 *
 * This is the trade-completion sibling of `setManual`: a finished trade is a
 * user-confirmed acquisition, so it always lands a "present" mark and carries
 * `source: "trade"` (which renders the green "T" badge in the Pokedex grid).
 * `source` is provenance only — under last-write-wins it does not rank the
 * write; the fresh trade is simply the newest statement about the cell.
 *
 * The one skip is a row already present FROM A TRADE: re-completing the same
 * trade is a no-op rather than a re-stamp. Any other prior (scan, or a manual
 * edit) is overwritten, since the completed trade is the most recent truth.
 * Returns true when a row was written or upgraded.
 */
export function markTradeCaught(
  variant: PokedexVariant,
  dexNumber: number,
  formId?: string | null,
): boolean {
  if (!Number.isFinite(dexNumber) || dexNumber < 1) return false;
  // pokedex-complete-dex: derived dexes are computed, never trade-marked.
  if (isDerivedVariant(variant)) return false;
  // Same trade gate as setManual: a completed trade cannot have produced a Lucky
  // of a species the game will not trade, so the row is impossible here too.
  // (Compare's trade leads already filter on isTradableSpecies, so this closes
  // the last write path rather than changing any reachable flow.)
  if (isImpossibleForVariant(variant, dexNumber)) {
    // eslint-disable-next-line no-console
    console.warn(
      `[dex] rejected trade-sourced ${variant} catch for #${dexNumber}, untradable species.`,
    );
    return false;
  }
  const store = safeReadStore();
  const key = keyFor(variant, dexNumber, formId);
  const prev = store[key];
  // Idempotency skip: only a row already present AND trade-sourced is left
  // as-is (re-completing the same trade is a no-op). A present scan/manual row
  // is re-stamped to trade — the completed trade is the newest statement and,
  // under last-write-wins, becomes the cell's provenance.
  if (prev && prev.state === "present" && prev.source === "trade") return false;
  const entry: DexEntryState = {
    variant, dexNumber, state: "present", source: "trade", updatedAt: Date.now(),
    ...(formId ? { formId } : {}),
  };
  store[key] = entry;
  // A cascading-variant catch implies a MAIN catch (one-directional), same as
  // the scan/manual paths. A valid Shadow/Purified trade likewise ticks MAIN.
  if (variant !== "pokemon") {
    ensureMainCaughtFromCascade(store, variant, dexNumber);
    ensureMainCaughtFromCuratedRoster(store, variant, dexNumber);
  }
  // The base-form mirror, same as the scan/manual paths run (2026-08-28 fix:
  // this was the ONE species-level present path that skipped it, so a Lucky
  // ticked from a completed trade never reached the form-keyed Compare rows
  // and the species stayed in the friends' missing-lucky copy list).
  const tradeMirrorRows = formId
    ? []
    : cascadeBaseSpeciesForm(store, variant, dexNumber, "trade", Date.now());
  safeWriteStore(store);
  emitChange();
  enqueuePending(key, entry);
  for (const c of tradeMirrorRows) enqueuePending(c.key, c.entry);
  if (isMainLinkedVariant(variant)) {
    const mainKey = keyFor("pokemon", dexNumber);
    const mainEntry = store[mainKey];
    if (mainEntry) enqueuePending(mainKey, mainEntry);
  }
  return true;
}

export function clearManual(
  variant: PokedexVariant,
  dexNumber: number,
  formId?: string | null,
): boolean {
  // Untick a Regional / Costume cell and it is the species page's own form row
  // that clears — the same row the tick wrote. See storageVariantFor.
  variant = storageVariantFor(variant);
  const store = safeReadStore();
  const key = keyFor(variant, dexNumber, formId);
  if (!store[key]) return false;
  // b19-dex-server-sync: upsert an "unknown / manual" tombstone so the server
  // knows the override was cleared. Using state="unknown" with source="manual"
  // signals "user explicitly cleared this" rather than "device never saw it".
  // The server upserts this row; because it carries a fresh updatedAt it wins
  // over any OLDER server entry under last-write-wins (source is provenance
  // only and does not rank it — a still-newer scan/edit will supersede it).
  const tombstone: DexEntryState = {
    variant, dexNumber, state: "unknown", source: "manual",
    updatedAt: Date.now(),
    ...(formId ? { formId } : {}),
  };
  enqueuePending(key, tombstone);
  // 2026-08-30 audit (Tier 1 #3): also record the RECONCILIATION tombstone,
  // like every sibling clear path does (setManual present->missing,
  // bulkSetVariantPresent's clear, reconcileUntradableLuckyCatches). The
  // enqueued server PUT above lives in an in-memory queue and dies with the
  // session; without this tombstone, a lost PUT meant the next pull saw no
  // local row AND no tombstone and re-imported the cleared catch.
  // mainPresentMissingSets even documents this tombstone as "the only
  // evidence that survives clearManual" - but nothing wrote it.
  // BASE clears only: tombstones are keyed (variant, dexId) with no form
  // dimension, so stamping one for a form-only clear would wrongly suppress
  // older BASE rows too. A form clear is covered by its enqueued PUT alone
  // (the pre-existing gap, now scoped to forms and documented).
  if (!formId) recordEntryTombstone(variant, dexNumber, tombstone.updatedAt);
  delete store[key];
  // Clearing the BASE cell clears the species' own base FORM row with it — the
  // same lockstep setManual keeps (see clearBaseSpeciesFormMirror). This is in
  // fact the commoner route to the drift: setManual(state:"unknown") delegates
  // straight here, and the reported account's stale mirrors sat under base rows
  // written by THIS function, so fixing only setManual would have left the main
  // leak open. The mirror gets the same "unknown / manual" tombstone, for the
  // same reason the base cell does.
  if (!formId) {
    for (const c of clearBaseSpeciesFormMirror(
      store, variant, dexNumber, "unknown", "manual", tombstone.updatedAt,
    )) {
      // The mirror is a cascade artefact, so it is DELETED locally exactly as the
      // base cell is, while the server gets the explicit tombstone. Leaving a
      // local row behind would just re-seed the drift on the next read.
      delete store[c.key];
      enqueuePending(c.key, { ...c.entry, state: "unknown", source: "manual" });
    }
  }
  safeWriteStore(store);
  emitChange();
  return true;
}

/**
 * Quick-update batch write (region × variant matrix). Marks a whole list of
 * ids caught (or clears them) for one variant in a SINGLE store read/write +
 * one DEX_STATE_EVENT, so completing a 150-species region is one render, not
 * 150. Semantics mirror the per-cell paths:
 *   - caught = true  → manual "present" rows (source="manual" is recorded as
 *     provenance; under last-write-wins a later scan/edit can still supersede
 *     them), plus BOTH Main linkages exactly like setManual: the curated
 *     Shadow/Purified roster cascade AND the cascading-variant
 *     (shiny/lucky/xxl/xxs/perfect) cascade (2026-08-30 audit: the second was
 *     missing, so a Quick-update/import shiny got no physical MAIN row).
 *     Impossible rows (untradable Lucky) are refused in-store. Ids already
 *     present are skipped.
 *   - caught = false → clears each existing row and enqueues the same
 *     "unknown / manual" server tombstone clearManual uses, so a re-scan of the
 *     region starts clean.
 *
 * Derived variants (Complete) are read-only and rejected. Returns the number of
 * rows actually changed.
 */
export function bulkSetVariantPresent(
  variant: PokedexVariant,
  ids: readonly number[],
  caught: boolean,
): number {
  if (isDerivedVariant(variant)) return 0;
  if (typeof localStorage === "undefined") return 0;
  const store = safeReadStore();
  const pending: Array<{ key: string; entry: DexEntryState }> = [];
  const now = Date.now();
  let changed = 0;
  for (const id of ids) {
    if (!Number.isFinite(id) || id < 1) continue;
    const key = keyFor(variant, id);
    const prev = store[key];
    if (caught) {
      // 2026-08-30 audit: enforce the impossibility gate IN the store like
      // setManual/markTradeCaught do, instead of relying on every call site
      // to pre-filter (the guards lived only at the two current callers, so
      // any future caller inherited zero protection). Clears stay ungated -
      // removing an impossible row is always right.
      if (isImpossibleForVariant(variant, id)) continue;
      if (prev?.state === "present") continue; // already caught — no-op
      const entry: DexEntryState = {
        variant,
        dexNumber: id,
        state: "present",
        source: "manual",
        updatedAt: now,
      };
      store[key] = entry;
      pending.push({ key, entry });
      // Curated Shadow/Purified catch also ticks the base Pokémon (All) dex,
      // one-directional + roster-gated, identical to setManual.
      if (ensureMainCaughtFromCuratedRoster(store, variant, id)) {
        const mainKey = keyFor("pokemon", id);
        const mainEntry = store[mainKey];
        if (mainEntry) pending.push({ key: mainKey, entry: mainEntry });
      }
      // 2026-08-30 audit (write-path asymmetry): the doc above promised
      // "exactly like setManual", but setManual runs BOTH cascades and this
      // path ran only the curated one - so a shiny/lucky/xxl/xxs marked via
      // Quick-update or the Poke Genie import got its variant row with NO
      // physical MAIN row, recreating the grid-silhouette-vs-count
      // disagreement setManual's own cascade note describes.
      if (ensureMainCaughtFromCascade(store, variant, id)) {
        const mainKey = keyFor("pokemon", id);
        const mainEntry = store[mainKey];
        if (mainEntry) pending.push({ key: mainKey, entry: mainEntry });
      }
      changed += 1;
      // Mirror the base into the species' secondary form cell(s) (Mega X→Y,
      // Urshifu) so marking a region caught fills both. No-op otherwise.
      for (const c of cascadeMegaGmaxForms(store, variant, id, "present", "manual", now)) {
        pending.push(c);
        changed += 1;
      }
      // Synced, not counted — see the note on the scan path above.
      for (const c of cascadeBaseSpeciesForm(store, variant, id, "manual", now)) {
        pending.push(c);
      }
    } else {
      // Clear the secondary form cell(s) too. THIS is the reported bug: removing
      // the Kanto region left Raichu Y / Mewtwo Y behind because the clear only
      // touched the base row. Run even when the base row is already gone, since a
      // form row can outlive it. The pushed `unknown` entry syncs the server
      // clear; the per-species (variant,id) reconciliation tombstone below covers
      // both cells, so no separate per-form tombstone is needed.
      const clearedForms = cascadeMegaGmaxForms(store, variant, id, "unknown", "manual", now);
      for (const c of clearedForms) {
        pending.push(c);
        changed += 1;
      }
      if (clearedForms.length > 0 && !prev) recordEntryTombstone(variant, id, now);
      if (!prev) continue; // nothing to clear on the base row
      // Same server tombstone clearManual writes: "user explicitly cleared".
      pending.push({
        key,
        entry: {
          variant,
          dexNumber: id,
          state: "unknown",
          source: "manual",
          updatedAt: now,
        },
      });
      // …and the local reconciliation record, which the server tombstone alone
      // does not provide. Deleting the row erases every trace of the clear from
      // the store, so without this the MAIN cascade re-asserted a catch the
      // trainer had just cleared — the species still owned as a Shiny counted
      // as caught again, and the grid and completion total disagreed. setManual
      // already records this on a present -> missing edit; the Quick-update
      // matrix reaches the same intent by a different route and needs it too.
      recordEntryTombstone(variant, id, now);
      delete store[key];
      // Clearing the base cell clears the species' own base FORM row with it,
      // the same lockstep clearManual keeps (2026-08-31 report): the bulk
      // clear used to leave the cascade-minted mirror behind as an orphaned
      // present row with no parent, and repairFormParentCascade's launch pass
      // then resurrected it into fresh variant + MAIN rows — a cleared
      // region literally came back on the next cold start. The mirror is a
      // cascade artefact, so it is deleted locally exactly as the base cell
      // is, while the server gets the explicit unknown/manual clear.
      for (const c of clearBaseSpeciesFormMirror(store, variant, id, "unknown", "manual", now)) {
        delete store[c.key];
        pending.push({ key: c.key, entry: { ...c.entry, state: "unknown", source: "manual" } });
      }
      changed += 1;
    }
  }
  if (changed > 0) {
    safeWriteStore(store);
    for (const p of pending) enqueuePending(p.key, p.entry);
    emitChange();
  }
  return changed;
}

// ── Server sync (b19-dex-server-sync) ─────────────────────────────────────

/**
 * b19-dex-server-sync internal: enqueue an entry into the pending map and
 * reschedule the debounce timer. The pending map is keyed by
 * `${variant}:${dexNumber}`; writing twice for the same key just overwrites
 * with the latest entry (idempotent).
 *
 * NOT exported: callers go through the mutation functions (applyScanObservation
 * etc.) which call this internally.
 */
function enqueuePending(key: string, entry: DexEntryState): void {
  pendingDexState.set(key, entry);
  if (debounceTimer !== null) clearTimeout(debounceTimer);
  // Fire-and-forget: the debounce flush must not propagate errors to the
  // mutation caller. If _boundApiFetch is not set yet (page not mounted),
  // the set accumulates and flushes on bindDexSyncApiFetch call.
  debounceTimer = setTimeout(() => {
    debounceTimer = null;
    if (_boundApiFetch) {
      void flushPendingDexState(_boundApiFetch);
    }
  }, DEX_SYNC_DEBOUNCE_MS);
}

/**
 * Module-level apiFetch reference bound by the Pokedex page on mount so the
 * debounced flush can fire without React context.
 */
let _boundApiFetch: ((method: string, url: string, data?: unknown) => Promise<Response>) | null = null;

/**
 * Wire the apiFetch function into the dex sync loop. Called once from Pokedex
 * mount so the debounced flush can reach the server without React context.
 */
export function bindDexSyncApiFetch(
  apiFetch: (method: string, url: string, data?: unknown) => Promise<Response>,
): void {
  _boundApiFetch = apiFetch;
  // If mutations accumulated before the page mounted, flush them now.
  if (pendingDexState.size > 0) {
    void flushPendingDexState(apiFetch);
  }
  // One-time sweep for entries stranded by the old variant-rejection wedge
  // (see runOneTimeManualRecovery). Fire-and-forget: it must never delay or
  // fail the bind, and it self-skips after one clean run.
  //
  // flushManualOverridesToServer takes a fetch-like (path, init); this binder
  // is handed a (method, url, data) client. Adapt here rather than changing
  // either signature — the body is already a plain object on this side, so
  // the adapter just unwraps it.
  void runOneTimeManualRecovery((path, init) =>
    apiFetch(
      init.method ?? "PUT",
      path,
      typeof init.body === "string" ? JSON.parse(init.body) : init.body,
    ),
  ).catch(() => {
    /* recovery is best-effort; the marker stays unset so it retries */
  });
}

/**
 * Server cap on a single PUT /api/dex/state body (TOO_MANY_ENTRIES past this).
 * Kept in step with the check in cloudflare/src/routes/dex.ts.
 */
const DEX_SYNC_BATCH = 500;

/**
 * b19-dex-server-sync: flush pending entries to PUT /api/dex/state.
 *
 * BATCHED, and it has to be. This sent the entire pending set as ONE body,
 * which the server rejects past 500 entries with 400 TOO_MANY_ENTRIES. The
 * client keeps its pending set on any non-2xx, and that set lives in memory
 * only — so an app restart discarded it. Net effect: any bulk write bigger
 * than 500 rows NEVER reached the server and was silently lost from the
 * backup, while looking perfectly fine on device.
 *
 * That is not a corner case. The Quick-update matrix marks a whole region (or
 * a whole dex) in one action, and each caught id can enqueue two rows — the
 * variant row plus the cascaded MAIN row — so a single tap can blow past the
 * cap. A device showing 918 Lucky against 320 on the server is exactly this.
 *
 * On 404/501 (migration not yet applied): stops and reports gap: true.
 * On any other non-2xx or network error: stops, reports what did land, and
 *   leaves the rest queued for the next mutation debounce.
 */
export async function flushPendingDexState(
  apiFetch: (method: string, url: string, data?: unknown) => Promise<Response>,
): Promise<{ posted: number; gap: boolean }> {
  const { posted, gap } = await chainPendingDexStateFlush(apiFetch);
  return { posted, gap };
}

/** Internal flush outcome. `failed` never reaches callers — see the wrapper. */
interface DexFlushOutcome {
  posted: number;
  gap: boolean;
  /** The attempt hit a wall (rejection, network, missing endpoint). */
  failed: boolean;
}

const NO_FLUSH_WORK: DexFlushOutcome = { posted: 0, gap: false, failed: false };

/** The flush currently talking to the server, or null. */
let flushInFlight: Promise<DexFlushOutcome> | null = null;
/** At most one follow-up waiting behind it. */
let flushQueued: Promise<DexFlushOutcome> | null = null;

/**
 * SINGLE-FLIGHT. Only one flush may be in the air at a time.
 *
 * WHY
 *   Five callers flush the same pending map — the mutation debounce, the mount
 *   bind, visibilitychange, pagehide, the page unmount and the one-shot
 *   resync-up — and entries are only retired from the map once their batch
 *   RESOLVES. A second flush starting inside that window therefore snapshots
 *   and re-sends rows the first flush has already put on the wire.
 *
 *   The server upserts by (account, variant, dexId, formId), so the duplicate
 *   pair raced: both requests read no row, both inserted, and the loser took a
 *   UNIQUE violation back as a 500. The device reads a 500 as a hard rejection,
 *   stops flushing, and leaves the rest of the queue in a map that does not
 *   survive a restart. In production that hit 22 of the 34 accounts with any
 *   dex data — worst case 1245 rows left queued.
 *
 *   flushPendingDexStateBlocking cancels the debounce TIMER, which does nothing
 *   about a flush already awaiting its PUT — so the hide/unmount paths were the
 *   likeliest source of the overlap.
 *
 * WHY A QUEUED FOLLOW-UP, NOT JUST SHARING THE IN-FLIGHT PROMISE
 *   A caller arriving mid-flush may be flushing entries the running snapshot
 *   never saw (that is exactly what pagehide is for). Handing it the in-flight
 *   promise would let those entries miss their ride and sit until the next
 *   mutation. So one follow-up run is queued, and every caller in that window
 *   coalesces onto it.
 *
 *   The follow-up does NOT re-run after a failed attempt: the next one would
 *   hit the same wall, and retrying here would double-count the failure in the
 *   health record. Recovery stays where it was — the next mutation's debounce.
 */
function chainPendingDexStateFlush(
  apiFetch: (method: string, url: string, data?: unknown) => Promise<Response>,
): Promise<DexFlushOutcome> {
  if (flushInFlight === null) {
    flushInFlight = runPendingDexStateFlush(apiFetch).finally(() => {
      flushInFlight = null;
    });
    return flushInFlight;
  }
  if (flushQueued === null) {
    flushQueued = flushInFlight
      // A throw here is already recorded by the run itself; the follow-up just
      // needs to know it finished.
      .catch(() => NO_FLUSH_WORK)
      .then((prev) => {
        flushQueued = null;
        if (prev.failed || prev.gap || pendingDexState.size === 0) return prev;
        return chainPendingDexStateFlush(apiFetch);
      });
  }
  return flushQueued;
}

async function runPendingDexStateFlush(
  apiFetch: (method: string, url: string, data?: unknown) => Promise<Response>,
): Promise<DexFlushOutcome> {
  if (pendingDexState.size === 0) return NO_FLUSH_WORK;

  // Snapshot key+entry pairs so a batch can be retired precisely. The old
  // code cleared the WHOLE map on success, which also discarded any mutation
  // that arrived while the request was in flight.
  const snapshot = Array.from(pendingDexState.entries());
  let posted = 0;

  for (let i = 0; i < snapshot.length; i += DEX_SYNC_BATCH) {
    const chunk = snapshot.slice(i, i + DEX_SYNC_BATCH);
    const entries = chunk.map(([, e]) => ({
      variant: e.variant,
      dexNumber: e.dexNumber,
      state: e.state,
      source: e.source,
      updatedAt: e.updatedAt,
      // PR-E2c-store: include formId only when present so old servers that
      // pre-date migration 0030 keep accepting the body (the field is
      // optional in the zod schema and ignored on legacy validators).
      ...(e.formId ? { formId: e.formId } : {}),
    }));

    try {
      const res = await apiFetch("PUT", "/api/dex/state", { entries });
      if (res.status === 404 || res.status === 501) {
        // Endpoint absent: no later batch fares better. Keep everything.
        recordSyncAttempt({
          posted,
          pendingAfter: pendingDexState.size,
          failure: { reason: "endpoint_missing", status: res.status },
        });
        return { posted, gap: true, failed: true };
      }
      if (!res.ok) {
        recordSyncAttempt({
          posted,
          pendingAfter: pendingDexState.size,
          failure: { reason: "rejected", status: res.status },
        });
        // A rejection is exactly what an operator needs to see, and a small
        // POST on another route is unaffected by whatever rejected this one.
        void reportSyncHealth(apiFetch).catch(() => { /* never surfaces */ });
        return { posted, gap: false, failed: true };
      }
      // Retire exactly what this batch sent. The identity check matters: if a
      // newer edit for the same cell landed during the await, the map now
      // holds a DIFFERENT object and must stay queued rather than be dropped.
      for (const [key, sent] of chunk) {
        if (pendingDexState.get(key) === sent) pendingDexState.delete(key);
      }
      posted += chunk.length;
      // 2026-10-13: scan-sourced rows reaching the server is the attribution
      // "first scan" moment, the same test referralRewards.hasCompletedScan
      // applies server-side. Once per device; the module keeps the flag.
      if (chunk.some(([, e]) => e.source === "scan")) {
        void import("./attribution")
          .then(({ noteScanRowsSynced }) => noteScanRowsSynced())
          .catch(() => undefined);
        // The same moment opens the feedback and review asks (lib/firstRunPrompts).
        void import("./firstRunPrompts")
          .then(({ noteFirstRunScan }) => noteFirstRunScan())
          .catch(() => undefined);
      }
    } catch {
      // Network failure: keep what has not been sent for the next retry.
      recordSyncAttempt({
        posted,
        pendingAfter: pendingDexState.size,
        failure: { reason: "network" },
      });
      return { posted, gap: false, failed: true };
    }
  }

  if (posted > 0 && typeof localStorage !== "undefined") {
    try { localStorage.setItem(LAST_SYNC_AT_KEY, String(Date.now())); } catch { /* quota */ }
  }
  // Every batch landed. pendingDexState.size is normally 0 here; anything left
  // is an edit made mid-flush, which keeps the backlog clock running.
  recordSyncAttempt({ posted, pendingAfter: pendingDexState.size });
  // Fire-and-forget; reportSyncHealth self-gates to transitions only.
  void reportSyncHealth(apiFetch).catch(() => { /* never surfaces */ });
  return { posted, gap: false, failed: false };
}

/**
 * b19-dex-server-sync: blocking variant for lifecycle callers (visibilitychange,
 * pagehide) that must await the flush before the page is backgrounded. Cancels
 * any pending debounce to avoid a double-PUT.
 */
export async function flushPendingDexStateBlocking(
  apiFetch: (method: string, url: string, data?: unknown) => Promise<Response>,
): Promise<{ posted: number; gap: boolean }> {
  if (debounceTimer !== null) {
    clearTimeout(debounceTimer);
    debounceTimer = null;
  }
  return flushPendingDexState(apiFetch);
}

/**
 * Re-upload the ENTIRE local dex to the server (restore path).
 *
 * Enqueues every entry currently in the local store and flushes it to
 * PUT /api/dex/state. This rebuilds a server dex that was wiped while the device
 * kept its local copy (the retired clean-slate wipe did exactly that — see
 * dexWipeMigration.ts / dexResyncUp.ts).
 *
 * Purely ADDITIVE: the server PUT upserts per entry and never deletes rows
 * absent from the body, so a device with a partial (or complete) local dex can
 * only add or refresh server rows, never remove them. A device with an empty
 * local dex enqueues nothing and is a no-op. Returns the flush result plus the
 * number of entries enqueued.
 */
export async function resyncLocalDexToServer(
  apiFetch: (method: string, url: string, data?: unknown) => Promise<Response>,
): Promise<{ posted: number; gap: boolean; enqueued: number }> {
  const store = safeReadStore();
  let enqueued = 0;
  for (const k in store) {
    enqueuePending(k, store[k]);
    enqueued += 1;
  }
  if (enqueued === 0) return { posted: 0, gap: false, enqueued: 0 };
  const res = await flushPendingDexState(apiFetch);
  return { ...res, enqueued };
}

// ── One-time backfill (b19-data-migration) ───────────────────────────────

const BACKFILL_STATUS_KEY = "nexus.dexState.backfillStatus.v1";

/**
 * Shape returned by POST /api/dex/backfill.
 */
interface BackfillResponse {
  status: "imported" | "skipped";
  reason?: "already_populated" | "no_source_data";
  pokemonRecordsScanned?: number;
  dexRecordsWritten?: number;
  existing?: number;
  serverNow: number;
}

/** Read the cached backfill status flag from localStorage. */
function readBackfillStatus(): string | null {
  if (typeof localStorage === "undefined") return null;
  try {
    return localStorage.getItem(BACKFILL_STATUS_KEY);
  } catch {
    return null;
  }
}

/** Write the backfill status flag to localStorage after a successful server call. */
function writeBackfillStatus(data: BackfillResponse): void {
  if (typeof localStorage === "undefined") return;
  let value: string;
  if (data.status === "imported") {
    value = `imported:${data.dexRecordsWritten ?? 0}`;
  } else if (data.reason === "already_populated") {
    value = "skipped:already_populated";
  } else {
    value = "skipped:no_source_data";
  }
  try {
    localStorage.setItem(BACKFILL_STATUS_KEY, value);
  } catch {
    /* quota -- in-memory state still valid for this session */
  }
}

/**
 * Trigger the server-side one-time backfill of dex_records from pokemon_records.
 * Idempotent on the server: subsequent calls after the first successful run
 * return status:"skipped" with reason:"already_populated".
 *
 * Call this BEFORE pullDexStateFromServer on app launch so the table is
 * populated by the time the client pulls. Gated on a localStorage flag so we
 * do not hit the endpoint on every launch once it has run once.
 */
export async function ensureDexBackfillFromRecords(
  apiFetch: (method: string, url: string, data?: unknown) => Promise<Response>,
): Promise<{ ran: boolean; written: number } | { gap: true }> {
  // Local cache key: nexus.dexState.backfillStatus.v1
  // Values: "imported:N" | "skipped:already_populated" | "skipped:no_source_data"
  // If any of these are present, skip the network call entirely.
  const cached = readBackfillStatus();
  if (cached) return { ran: false, written: 0 };
  try {
    const res = await apiFetch("POST", "/api/dex/backfill");
    if (res.status === 404 || res.status === 501) {
      // Endpoint not deployed yet (migration gap). Try again next launch.
      return { gap: true };
    }
    if (!res.ok) throw new Error(`backfill failed: ${res.status}`);
    const data = await res.json() as BackfillResponse;
    writeBackfillStatus(data);
    return { ran: data.status === "imported", written: data.dexRecordsWritten ?? 0 };
  } catch (err) {
    // Network error: do NOT write the cache flag, retry next launch.
    console.warn("dexStateStore: backfill request failed, will retry on next launch", err);
    return { gap: true };
  }
}

/**
 * b19-dex-server-sync: pull the full dex state from the server and merge into
 * localStorage using the same conflict resolver as the server PUT handler.
 *
 * Called once per session from the Pokedex page mount effect (gated by isPro
 * if applicable) to bring in entries from other devices. Pass since=null for
 * a full pull; pass a previoussync timestamp for a delta pull.
 *
 * localStorage stays the source of truth: server rows only overwrite local rows
 * when the conflict resolver returns "replace". One emitChange fires at the end
 * if anything was merged.
 */
/** Variants a pulled server row may legitimately carry: every scannable
 * variant plus the manual-only ones. Derived variants (complete) are computed
 * locally and never stored; anything else is server-side junk. */
const KNOWN_PULL_VARIANTS: ReadonlySet<string> = new Set<string>([
  ...ALL_VARIANTS,
  ...MANUAL_ONLY_VARIANTS,
]);

/**
 * Validation for rows arriving FROM the server (2026-08-30 audit, Tier 1 #2).
 * Mirrors the union of the local write paths' gates: scan writes check
 * isVariantAvailable, manual/trade writes check isImpossibleForVariant, and
 * every path refuses derived variants. Returns a reject reason, or null when
 * the row may merge. Negative states pass the availability gates (a removal
 * is always safe to import); malformed identity is rejected regardless.
 */
function pulledRowRejectReason(remote: {
  variant: string;
  dexNumber: number;
  state: string;
}): string | null {
  if (!KNOWN_PULL_VARIANTS.has(remote.variant)) {
    return isDerivedVariant(remote.variant as PokedexVariant)
      ? "derived-variant"
      : "unknown-variant";
  }
  if (!Number.isInteger(remote.dexNumber) || remote.dexNumber < 1) {
    return "bad-dex-number";
  }
  if (remote.state === "present") {
    const variant = remote.variant as PokedexVariant;
    if (isImpossibleForVariant(variant, remote.dexNumber)) return "impossible-for-variant";
    if (!isVariantAvailable(variant, remote.dexNumber)) return "unreleased-species";
  }
  return null;
}

export async function pullDexStateFromServer(
  apiFetch: (method: string, url: string, data?: unknown) => Promise<Response>,
  since?: number | null,
): Promise<{ pulled: number; gap: boolean; proRequired?: boolean; backupCount?: number }> {
  // PR V: the interim full-pull-skip gate (which returned early while
  // REMOTE_WIPE_PENDING_KEY was set) has been REPLACED by per-row tombstone
  // filtering below. The pull now always proceeds; tombstones decide per-row
  // whether an incoming server row represents an intentional client-side
  // removal that has not yet propagated to the server. This keeps the wipe
  // durable even AFTER the deferred server DELETE succeeds and clears the
  // pending marker (the interim gate disappeared at that point, leaving nothing
  // to stop a future stale re-import). The REMOTE_WIPE_PENDING_KEY marker still
  // exists, but it now only drives PR U's deferred DELETE task — not this pull.
  try {
    const url = since != null ? `/api/dex/state?since=${since}` : "/api/dex/state";
    const res = await apiFetch("GET", url);
    if (res.status === 404 || res.status === 501) return { pulled: 0, gap: true };
    // Cross-device restore/sync is Pro-gated (backup via PUT is free for all).
    // A 403 is NOT an error and must NEVER touch local state: this device's
    // local dex stays authoritative. Surface proRequired + the server backup
    // count so the UI can offer a "restore your backed-up dex with Pro" prompt.
    if (res.status === 403) {
      let backupCount = 0;
      try {
        backupCount = ((await res.json()) as { backupCount?: number })?.backupCount ?? 0;
      } catch {
        /* body optional — default to 0 */
      }
      return { pulled: 0, gap: true, proRequired: true, backupCount };
    }
    if (!res.ok) return { pulled: 0, gap: false };

    const body = await res.json() as {
      entries: Array<{
        variant: string;
        dexNumber: number;
        state: string;
        source: string;
        updatedAt: number;
        formId?: string | null;
      }>;
      serverNow: number;
    };

    if (!body?.entries || !Array.isArray(body.entries)) return { pulled: 0, gap: false };

    const store = safeReadStore();
    // PR V: snapshot the tombstone blob once for the whole pull. Each incoming
    // row is checked against it BEFORE the last-writer-wins merge.
    const tombstones = readTombstones();
    let merged = 0;
    let touched = false;
    let rejected = 0;

    for (const remote of body.entries) {
      // 2026-08-30 audit (Tier 1 #2): the pull used to be the ONE dex write
      // path with no validation - a bad server row (untradable Lucky,
      // unreleased species, a derived or unknown variant) was written straight
      // into local storage and then re-pushed by the next flush, making bad
      // server data self-perpetuating. Apply the same gates the local write
      // paths enforce, BEFORE any tombstone bookkeeping so a rejected row can
      // never clear a tombstone either. Negations ("missing"/"unknown") are
      // deliberately not availability-gated - a removal statement is always
      // safe to import; only positive "present" assertions can inflate.
      const rejectReason = pulledRowRejectReason(remote);
      if (rejectReason != null) {
        rejected += 1;
        continue;
      }
      // PR-E2c-store: form-level rows live at a distinct key. The remote
      // payload may include formId; if absent / null we use the legacy
      // species-row key shape so old localStorage keys are preserved.
      const remoteFormId = remote.formId ?? undefined;
      // A row this device (or an older build) filed under a form dex is the
      // same fact as MAIN's form row, so it lands there. This is what migrates
      // a trainer's existing Regional / Costume rows: they fold in as they
      // arrive, and the boot repair (foldFormDexRowsIntoMain) catches the ones
      // already sitting in local storage. See storageVariantFor.
      const remoteVariant = storageVariantFor(remote.variant as PokedexVariant);
      const key = keyFor(remoteVariant, remote.dexNumber, remoteFormId);
      // PR V: drop rows superseded by a newer client-side removal (per-entry or
      // bulk wipe) tombstone. This is what keeps a wiped local store from being
      // silently re-polluted by stale server rows after the deferred DELETE has
      // already cleared the pending marker.
      // Checked under the NORMALISED variant so a folded form-dex row obeys the
      // same tombstone its MAIN sibling would (they are one cell now).
      if (isRowTombstoned(tombstones, remoteVariant, remote.dexNumber, remote.updatedAt)) {
        // eslint-disable-next-line no-console
        console.log("[dex-tombstone] dropped incoming row", {
          variant: remoteVariant,
          dexNumber: remote.dexNumber,
          rowUpdatedAt: remote.updatedAt,
          bulkAt: tombstones.bulkAt,
        });
        continue;
      }
      // The server row is newer than any tombstone for this key: a legitimate
      // re-add (the user re-caught the species on another device after the
      // local removal). Drop the now-stale per-entry tombstone so it cannot
      // interfere with future pulls, then apply the row normally.
      //
      // BASE ROWS ONLY (2026-08-30 audit, Tier 1 #4): tombstones are keyed
      // (variant, dexId) with no form dimension, so letting a FORM-qualified
      // row clear the species tombstone resurrected the stale BASE row on the
      // next pull - a fresh lucky:647:keldeo cleared lucky:647's tombstone,
      // then the old lucky:647 base row (older than the tombstone, previously
      // dropped) merged back. A form row does not speak for the base cell;
      // only a newer base row supersedes the species tombstone.
      if (remoteFormId == null) {
        clearEntryTombstone(remoteVariant, remote.dexNumber);
      }
      const local = store[key];
      if (local) {
        const resolution = resolveDexEntryConflict(
          { source: local.source, updatedAt: local.updatedAt },
          { source: remote.source, updatedAt: remote.updatedAt },
        );
        if (resolution === "keep") continue;
      }
      store[key] = {
        variant: remoteVariant,
        dexNumber: remote.dexNumber,
        state: remote.state as CellState,
        source: remote.source as "scan" | "manual" | "trade",
        updatedAt: remote.updatedAt,
        ...(remoteFormId ? { formId: remoteFormId } : {}),
      };
      merged += 1;
      touched = true;
    }

    if (touched) {
      safeWriteStore(store);
      emitChange();
    }
    if (rejected > 0) {
      // eslint-disable-next-line no-console
      console.warn(`[dex-pull] rejected ${rejected} invalid server row(s) (validation gate)`);
    }

    // Store the serverNow cursor so next calls can use ?since for deltas.
    if (body.serverNow && typeof localStorage !== "undefined") {
      try { localStorage.setItem(LAST_SYNC_AT_KEY, String(body.serverNow)); } catch { /* quota */ }
    }

    return { pulled: merged, gap: false };
  } catch {
    return { pulled: 0, gap: false };
  }
}

/**
 * Push every manual override to the server via PUT /api/dex/state.
 *
 * Now has a real server implementation (b19-dex-server-sync). Previously
 * pointed at the aspirational /api/dex/variant-state (POST). Updated to
 * use the canonical /api/dex/state (PUT) with a manual-only filter.
 *
 * Unlike flushPendingDexState this reads the WHOLE store rather than the
 * pending set, which is what makes it a recovery path: pendingDexState is an
 * in-memory Map that empties on every app restart, so an entry that failed to
 * send and then survived a restart is unreachable by the normal debounce path.
 *
 * A 404/501 gap is returned when the migration has not yet been applied
 * (Build 221 cycle). On any other non-2xx or network failure, returns
 * gap: true so callers show "saved on this device only".
 *
 * BATCHED: the server rejects a body of more than 500 entries with
 * TOO_MANY_ENTRIES. This used to send every override in one request, so any
 * trainer with a large manual history got a 400 and silently re-pushed
 * nothing — precisely the users who most needed a recovery path.
 */
const MANUAL_FLUSH_BATCH = 500;

export async function flushManualOverridesToServer(
  apiFetch: (path: string, init: RequestInit) => Promise<Response>,
): Promise<{ posted: number; gap: boolean }> {
  const store = safeReadStore();
  const overrides = Object.values(store).filter((e) => e.source === "manual");
  if (overrides.length === 0) return { posted: 0, gap: false };

  let posted = 0;
  for (let i = 0; i < overrides.length; i += MANUAL_FLUSH_BATCH) {
    const chunk = overrides.slice(i, i + MANUAL_FLUSH_BATCH);
    try {
      const res = await apiFetch("/api/dex/state", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          entries: chunk.map((e) => ({
            variant: e.variant,
            dexNumber: e.dexNumber,
            state: e.state,
            source: e.source,
            updatedAt: e.updatedAt,
            ...(e.formId ? { formId: e.formId } : {}),
          })),
        }),
      });
      if (res.status === 404 || res.status === 501) {
        // Endpoint absent: no later batch will fare better, so stop here and
        // report the gap rather than hammering the server.
        return { posted, gap: true };
      }
      if (!res.ok) {
        return { posted, gap: true };
      }
      posted += chunk.length;
    } catch {
      // Network failure mid-run: report what did land. localStorage stays
      // authoritative, and the caller leaves the retry marker unset.
      return { posted, gap: true };
    }
  }
  return { posted, gap: false };
}

/**
 * One-time recovery re-push, run once per device.
 *
 * WHY THIS EXISTS
 *   The sync endpoint used to reject four variants the client can store
 *   (mega / gmax / shundo / shinyLucky). PUT validates `entries` as an array,
 *   so one such entry failed the WHOLE batch with 400, and flushPendingDexState
 *   keeps its pending set on a non-2xx. That set is in-memory, so an app
 *   restart discarded it: those catches were never sent, are no longer queued,
 *   and the debounce path will never retry them. They are stranded on device.
 *
 *   Opening the schema lets NEW marks through but cannot lift the old ones.
 *   This sweeps the store once so a device that was wedged catches its backup
 *   up, then never runs again.
 *
 * WHY IT IS SAFE TO RE-SEND ROWS THE SERVER ALREADY HAS
 *   Every entry carries its original device timestamp, and the server resolves
 *   by last-write-wins on that value. Re-sending an older row is a no-op: it
 *   loses to whatever is already stored. Nothing can be clobbered by this.
 *
 * The marker is only set on a clean run, so a device that is offline or
 * mid-failure retries on the next launch instead of giving up.
 */
const MANUAL_RECOVERY_KEY = "nexus.dexState.manualRecovery.v1";

export async function runOneTimeManualRecovery(
  apiFetch: (path: string, init: RequestInit) => Promise<Response>,
): Promise<{ ran: boolean; posted: number }> {
  if (typeof localStorage === "undefined") return { ran: false, posted: 0 };
  try {
    if (localStorage.getItem(MANUAL_RECOVERY_KEY)) return { ran: false, posted: 0 };
  } catch {
    return { ran: false, posted: 0 };
  }

  const { posted, gap } = await flushManualOverridesToServer(apiFetch);
  if (gap) {
    // Leave the marker unset so the next launch tries again.
    return { ran: false, posted };
  }
  try {
    localStorage.setItem(MANUAL_RECOVERY_KEY, String(Date.now()));
  } catch {
    /* quota — the re-push already succeeded; worst case it runs again */
  }
  return { ran: true, posted };
}

// ── Reset (test + admin) ─────────────────────────────────────────────────

/** Expose the pending map size for tests only. */
export function _getPendingCountForTests(): number {
  return pendingDexState.size;
}

/** Clear the entire store. Used by tests and a Settings admin tool. */
export function _resetDexStateForTests(): void {
  lastActiveVariantCache = null;
  // b19-dex-server-sync: clear in-memory sync state so tests are isolated.
  pendingDexState.clear();
  if (debounceTimer !== null) { clearTimeout(debounceTimer); debounceTimer = null; }
  // Drop the single-flight handles too: a promise left over from the previous
  // test would coalesce the next test's flush onto a run it cannot see.
  flushInFlight = null;
  flushQueued = null;
  _boundApiFetch = null;
  // Perf caches must be dropped alongside the underlying store.
  invalidateStoreCache();
  totalsSnapshotCache.clear();
  presentIdsSnapshotCache.clear();
  if (typeof localStorage === "undefined") return;
  localStorage.removeItem(STORAGE_KEY);
  localStorage.removeItem(LAST_VARIANT_KEY);
  localStorage.removeItem(LAST_SYNC_AT_KEY);
  localStorage.removeItem(BACKFILL_STATUS_KEY);
  emitChange();
}

/**
 * b13c.7 — clear the LOCAL Nexus Dex state synchronously. This is the
 * authoritative reset: localStorage is where the dex lives, so wiping the key
 * and emitting a change event leaves the device with an empty dex immediately.
 * The optional remote clear (`clearNexusDexRemote`) is a best-effort follow-up
 * for forward-compat with a future D1-backed variant-state table.
 */
export function resetNexusDexLocal(): void {
  lastActiveVariantCache = null;
  pendingDexState.clear();
  if (debounceTimer !== null) { clearTimeout(debounceTimer); debounceTimer = null; }
  // Perf caches must be dropped alongside the underlying store; also refresh the
  // totals snapshot so the (now empty) dex reads zero immediately.
  invalidateStoreCache();
  totalsSnapshotCache.clear();
  presentIdsSnapshotCache.clear();
  if (typeof localStorage === "undefined") return;
  localStorage.removeItem(STORAGE_KEY);
  localStorage.removeItem(LAST_VARIANT_KEY);
  localStorage.removeItem(LAST_SYNC_AT_KEY);
  emitChange();
  commitDexTotals();
}

/**
 * b13c.7 — best-effort remote clear of the user's Nexus Dex state.
 *
 * Now has a real server implementation (b19-dex-server-sync). Previously
 * pointed at the aspirational /api/dex/variant-state (DELETE). Updated to
 * use the canonical DELETE /api/dex/state which hard-deletes all dex_records
 * rows for the account and writes a dex_state_reset audit log entry.
 *
 * A 404/501 gap is returned when the migration has not yet been applied
 * (Build 221 cycle); a missing endpoint never blocks the local wipe.
 * Returns { cleared: true } only on a 2xx.
 */
export async function clearNexusDexRemote(
  apiFetch: (method: string, url: string, data?: unknown) => Promise<Response>,
): Promise<{ cleared: boolean; gap: boolean }> {
  try {
    const res = await apiFetch("DELETE", "/api/dex/state");
    if (res.status === 404 || res.status === 501) return { cleared: false, gap: true };
    if (!res.ok) return { cleared: false, gap: true };
    return { cleared: true, gap: false };
  } catch {
    return { cleared: false, gap: true };
  }
}

/**
 * b13c.7 — full Nexus Dex reset: clear local state (always) and attempt the
 * best-effort remote clear. Local wipe happens first and unconditionally so the
 * UI reflects an empty dex even when offline or the endpoint is absent.
 */
export async function resetNexusDex(
  apiFetch?: (method: string, url: string, data?: unknown) => Promise<Response>,
): Promise<{ cleared: boolean; gap: boolean }> {
  resetNexusDexLocal();
  if (!apiFetch) return { cleared: false, gap: true };
  return clearNexusDexRemote(apiFetch);
}

/** Guard flag for the one-shot base-form mirror repair (2026-08-11). */
// v2 (2026-08-28): the repair now also CREATES missing mirrors (a base present
// row whose mirror form row does not exist — the markTradeCaught gap, plus the
// first-form fallback for base-less groups), so it must run once more on every
// device that already burned the v1 flag.
const BASE_FORM_MIRROR_REPAIR_FLAG = "dex_base_form_mirror_repair_v2";

/**
 * ONE-SHOT REPAIR — stale base-species-form mirrors (2026-08-11).
 *
 * Before `clearBaseSpeciesFormMirror` existed the mirror was write-only: ticking
 * a species wrote both the base row and the species' own base FORM row, while
 * unticking wrote only the base row. Every correction therefore left a base-form
 * row asserting a catch the trainer had already taken back. 182 such rows across
 * 3 accounts when this was found.
 *
 * Those rows are wrong on the DEVICE, not just the server, so a server-side
 * cleanup alone would be undone the moment the device re-pushed one. This walks
 * the local store, brings every stale mirror into line with its base row, and
 * enqueues each correction for the normal debounced PUT — so the device repairs
 * itself and pushes the truth up, in the direction sync already trusts.
 *
 * Conservative by construction:
 *   - only fires where a base row EXISTS and is NOT present, and its mirror IS
 *     present — the exact drift shape, never a general reconciliation;
 *   - only when the base row is NEWER than the mirror, so a genuine catch
 *     recorded on the form cell after the base was cleared is left alone;
 *   - a hand-set (`source: "manual"`) mirror is never overwritten by a repair;
 *   - Mega/G-Max and regional siblings are untouched (see the mirror helpers).
 *
 * Idempotent and flag-guarded, so it costs one localStorage read per launch
 * after the first. Returns the number of rows repaired (0 when already done),
 * for the caller's log and for tests.
 */
export function repairBaseSpeciesFormMirrors(opts?: { force?: boolean }): number {
  try {
    if (typeof window !== "undefined" && !opts?.force) {
      if (window.localStorage?.getItem(BASE_FORM_MIRROR_REPAIR_FLAG) === "1") return 0;
    }
    const store = safeReadStore();
    const repaired: Array<{ key: string; entry: DexEntryState }> = [];

    // v2 CREATE pass: a species-level present row whose mirror form row was
    // never written (markTradeCaught skipped the cascade until 2026-08-28,
    // and base-less groups had no mirror target before the first-form
    // fallback). Additive only: an existing mirror row of ANY state is left
    // to the drift pass below, so a hand-set form answer is never overwritten.
    for (const [, entry] of Object.entries(store)) {
      if (!entry || entry.state !== "present" || entry.formId) continue;
      if (isMegaGmaxVariant(entry.variant)) continue;
      const group = FORM_GROUPS_BY_SPECIES_ID.get(entry.dexNumber);
      if (!group) continue;
      const mirrorFormId = mirrorFormIdFor(group);
      if (!mirrorFormId) continue;
      const mirrorKey = keyFor(entry.variant, entry.dexNumber, mirrorFormId);
      if (store[mirrorKey]) continue;
      const created: DexEntryState = {
        variant: entry.variant,
        dexNumber: entry.dexNumber,
        state: "present",
        source: entry.source,
        updatedAt: entry.updatedAt,
        formId: mirrorFormId,
      };
      store[mirrorKey] = created;
      repaired.push({ key: mirrorKey, entry: created });
    }

    for (const [key, entry] of Object.entries(store)) {
      if (!entry || entry.state !== "present" || !entry.formId) continue;
      if (isMegaGmaxVariant(entry.variant)) continue;
      // Only the species' OWN base form mirrors the base cell; a regional or
      // named sibling is a different Pokémon and a real trade lead.
      const group = FORM_GROUPS_BY_SPECIES_ID.get(entry.dexNumber);
      if (!group || entry.formId !== group.speciesName) continue;
      // A mirror the trainer set by hand is their answer, not a cascade's.
      if (entry.source === "manual") continue;

      const baseEntry = store[keyFor(entry.variant, entry.dexNumber)];
      // No base row, or the base agrees — nothing drifted here.
      if (!baseEntry || baseEntry.state === "present") continue;
      // The base row must be the LATER statement, or this mirror may be a real
      // catch recorded after the base was cleared.
      if (!(baseEntry.updatedAt > entry.updatedAt)) continue;

      const fixed: DexEntryState = {
        ...entry,
        state: baseEntry.state,
        source: baseEntry.source,
        updatedAt: baseEntry.updatedAt,
      };
      store[key] = fixed;
      repaired.push({ key, entry: fixed });
    }

    if (repaired.length > 0) {
      safeWriteStore(store);
      emitChange();
      for (const r of repaired) enqueuePending(r.key, r.entry);
    }
    try {
      if (typeof window !== "undefined") {
        window.localStorage?.setItem(BASE_FORM_MIRROR_REPAIR_FLAG, "1");
      }
    } catch {
      /* quota — worst case the repair re-runs next launch, which is idempotent */
    }
    if (repaired.length > 0) {
      // eslint-disable-next-line no-console
      console.log("[dex-repair] base-form mirrors corrected", { rows: repaired.length });
    }
    return repaired.length;
  } catch {
    // A repair failure must never block app launch or a dex read.
    return 0;
  }
}

const FORM_PARENT_CASCADE_REPAIR_FLAG = "dex_form_parent_cascade_repair_v1";

/**
 * ONE-SHOT REPAIR — form catches stranded without their species rows
 * (2026-08-29, "post-event quick adds never reached my Shiny Dex").
 *
 * The parent-species cascade ("any form caught means the species is caught",
 * computeFormCascade) has not always run for every path that writes a form
 * row: markTradeCaught skipped the cascade until 2026-08-28, and sub-dex form
 * ticks predating PR-E2c-cascade wrote the form row alone. A trainer who
 * recorded an event costume or regional through one of those paths owns the
 * FORM row, while the Shiny/Main dex cell above it still reads as missing —
 * the mark looks lost even though it is sitting right there in the store.
 *
 * So this sweeps every present form row once and re-runs the same cascade
 * engine the live write path uses, with one extra guard the live path does
 * not need: a base row carrying a LATER timestamp than the form row is a
 * newer statement (typically a deliberate clear) and is left alone. The live
 * cascade may trust "the write happening right now is newest"; a repair
 * replaying history may not.
 *
 * Cascaded rows are stamped with the FORM row's own updatedAt, not the repair
 * time, so server-side last-write-wins ordering stays truthful. Idempotent,
 * flag-guarded, and enqueued for sync like every other write.
 */
export function repairFormParentCascade(opts?: { force?: boolean }): number {
  try {
    if (typeof window !== "undefined" && !opts?.force) {
      if (window.localStorage?.getItem(FORM_PARENT_CASCADE_REPAIR_FLAG) === "1") return 0;
    }
    const store = safeReadStore();
    const repaired: Array<{ key: string; entry: DexEntryState }> = [];

    for (const entry of Object.values(store)) {
      if (!entry || entry.state !== "present" || !entry.formId) continue;
      // Mega/G-Max forms are independent dex cells, never a species catch —
      // the same exemption the live setManual cascade applies.
      if (isMegaGmaxVariant(entry.variant)) continue;
      const writes = computeFormCascade({
        sourceVariant: entry.variant,
        dexNumber: entry.dexNumber,
        formId: entry.formId,
        state: "present",
        readEntry: (v, n, fid) => store[keyFor(v, n, fid)],
        now: () => entry.updatedAt,
      });
      for (const w of writes) {
        const prev = store[w.key];
        // A row restated AFTER this form catch (a later clear, a later scan)
        // is the newer truth; only rows older than the form catch, or absent
        // altogether, were left behind by the missing cascade.
        if (prev && prev.updatedAt >= entry.updatedAt) continue;
        // 2026-08-30 audit: the replay must honour the same impossibility
        // gate the live write paths enforce - a surviving untradable-Lucky
        // FORM row used to re-mint the base lucky row that
        // reconcileUntradableLuckyCatches exists to delete, with launch
        // ordering as the only thing saving the day.
        if (
          w.entry.state === "present" &&
          isImpossibleForVariant(w.entry.variant, w.entry.dexNumber)
        ) {
          continue;
        }
        store[w.key] = w.entry;
        repaired.push({ key: w.key, entry: w.entry });
      }
    }

    if (repaired.length > 0) {
      safeWriteStore(store);
      emitChange();
      for (const r of repaired) enqueuePending(r.key, r.entry);
    }
    try {
      if (typeof window !== "undefined") {
        window.localStorage?.setItem(FORM_PARENT_CASCADE_REPAIR_FLAG, "1");
      }
    } catch {
      /* quota — worst case the repair re-runs next launch, which is idempotent */
    }
    if (repaired.length > 0) {
      // eslint-disable-next-line no-console
      console.log("[dex-repair] form parent cascade filled", { rows: repaired.length });
    }
    return repaired.length;
  } catch {
    // A repair failure must never block app launch or a dex read.
    return 0;
  }
}

const FORM_DEX_FOLD_REPAIR_FLAG = "dex_form_dex_fold_repair_v1";

/**
 * Fold the legacy Regional / Costume rows into MAIN's form rows (2026-09-01).
 *
 * The form dexes shipped on 2026-08-31 writing their own `regional:` /
 * `costume:` namespace. They are now a VIEW over MAIN's form rows
 * (storageVariantFor), so every tick a trainer already made on one of those
 * dexes has to move to the row the whole app now reads, or their work
 * disappears the moment this build lands.
 *
 * The same contract as the repairs above:
 *
 *   - ADDITIVE ONLY. A legacy `present` row lights `pokemon:<dex>:<formId>` when
 *     that row is not already present; nothing is ever downgraded, so a catch
 *     recorded since on the species page wins and a legacy `missing` row cannot
 *     un-catch anything.
 *   - The legacy rows are LEFT WHERE THEY ARE rather than deleted. Nothing
 *     reads them any more, deleting them would need tombstones to stop the
 *     server handing them back, and an inert key costs a few bytes. Rows that
 *     do come back from the server are folded on arrival by the pull.
 *   - The fold is enqueued for sync, so the trainer's other devices get the
 *     migrated rows without each having to run its own fold.
 *
 * Idempotent and flag-guarded; returns the number of rows folded.
 */
export function foldFormDexRowsIntoMain(opts?: { force?: boolean }): number {
  try {
    if (typeof window !== "undefined" && !opts?.force) {
      if (window.localStorage?.getItem(FORM_DEX_FOLD_REPAIR_FLAG) === "1") return 0;
    }
    const store = safeReadStore();
    const folded: Array<{ key: string; entry: DexEntryState }> = [];

    for (const entry of Object.values(store)) {
      if (!entry || entry.state !== "present" || !entry.formId) continue;
      if (!isFormDexVariant(entry.variant)) continue;
      const mainKey = keyFor("pokemon", entry.dexNumber, entry.formId);
      if (store[mainKey]?.state === "present") continue;
      const moved: DexEntryState = {
        variant: "pokemon",
        dexNumber: entry.dexNumber,
        state: "present",
        source: entry.source,
        // The catch's own timestamp travels with it: a species-page edit made
        // after this tick must still be the later statement.
        updatedAt: entry.updatedAt,
        formId: entry.formId,
      };
      store[mainKey] = moved;
      folded.push({ key: mainKey, entry: moved });
    }

    if (folded.length > 0) {
      safeWriteStore(store);
      emitChange();
      for (const f of folded) enqueuePending(f.key, f.entry);
    }
    try {
      if (typeof window !== "undefined") {
        window.localStorage?.setItem(FORM_DEX_FOLD_REPAIR_FLAG, "1");
      }
    } catch {
      /* quota — worst case the fold re-runs next launch, which is idempotent */
    }
    if (folded.length > 0) {
      // eslint-disable-next-line no-console
      console.log("[dex-repair] form dex rows folded into main", { rows: folded.length });
    }
    return folded.length;
  } catch {
    // A repair failure must never block app launch or a dex read.
    return 0;
  }
}
